import type { Decor, Rect } from '../world/mapdef';
import { T } from '../world/tiles';
import { S, hasGame } from '../core/game';
import { block, ellipse, hash, rect, shade, shadow, type Ctx } from './paint';
import { wind } from './nature';
import { registerPropDrawers } from './propRegistry';
import { drawCharacter, type CharLook, type Facing, type Pose } from './sprites';
import { isNight, line, poly, sign, stall, steam, txt } from './propsHualien';
import { LIFE_ANIMATED, LOOKS, drawLife, person } from './streetLife';

/**
 * Kaohsiung (kaohsiung package): the THSR Zuoying station and its wave roof, arcaded shophouses and
 * apartment blocks, the Dragon and Tiger Pagodas, the Spring and Autumn Pavilions and the giant Xuantian
 * statue on Lotus Pond, Formosa Boulevard's glass shells over the Dome of Light, Liuhe Night Market's
 * stalls and gate, Love River's boats, heart lamps and night reflections, Pier-2's murals, containers,
 * giant statues and the circular light rail, and the harbour: quay, Cijin ferry, fishing boats, kites,
 * gulls and the 85 Sky Tower. Big pictures are cached in bitmaps; small life is drawn every frame.
 */

const now = () => (hasGame() ? S().time : 12 * 60);
const h1 = (a: number, b: number) => hash(Math.round(a), Math.round(b), 503);

// ---------------------------------------------------------------------------
// Bitmap cache
// ---------------------------------------------------------------------------

const BITMAPS = new Map<string, { cv: HTMLCanvasElement; s: number }>();

/** Paint once into an offscreen bitmap (per zoom step) and blit; `x0,y0,w,h` is the world rect. */
function cachedArt(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void): void {
  const m = c.getTransform();
  const s = Math.min(3, Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2));
  let e = BITMAPS.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    BITMAPS.set(key, e);
  }
  c.drawImage(e.cv, x0, y0, w, h);
}

/** Cached picture anchored at its bottom centre: `paint` draws with (0,0) at the anchor, y up is negative. */
function art(c: Ctx, key: string, cx: number, by: number, w: number, h: number, paint: (c: Ctx, night: boolean) => void, nightAware = true): void {
  const night = nightAware && isNight();
  cachedArt(c, `${key}:${night ? 'n' : 'd'}`, cx - w / 2 - 6, by - h, w + 12, h + 10, (cc) => {
    cc.translate(cx, by);
    paint(cc, night);
  });
}

/** One 90 px slice (`k`, 0 = bottom) of a tall cached picture whose full height is `h`. */
function slice(c: Ctx, key: string, cx: number, by: number, w: number, h: number, k: number, paint: (c: Ctx, night: boolean) => void): void {
  const night = isNight();
  const m = c.getTransform();
  const s = Math.min(3, Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2));
  const kk = `${key}:${night ? 'n' : 'd'}`;
  let e = BITMAPS.get(kk);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil((w + 12) * s);
    cv.height = Math.ceil((h + 10) * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(w / 2 + 6, h);
    paint(cc, night);
    e = { cv, s };
    BITMAPS.set(kk, e);
  }
  const top = h - 90 * (k + 1);
  const sy = Math.max(0, top) * s, sh = (k === 0 ? 100 : 90) * s - (top < 0 ? -top * s : 0);
  c.drawImage(e.cv, 0, sy, e.cv.width, sh, cx - w / 2 - 6, by - 90 * (k + 1) + Math.max(0, -top), w + 12, sh / s);
}

// ---------------------------------------------------------------------------
// Shared bits: windows, roofs, signs, little people
// ---------------------------------------------------------------------------

/** A window: glass by day, warm light (or dark) at night; `seed` picks which rooms are lit. */
function win(c: Ctx, x: number, y: number, w: number, h: number, night: boolean, seed: number, frame = '#e6e2d8'): void {
  rect(c, frame, x - 0.8, y - 0.8, w + 1.6, h + 1.6);
  if (night) {
    const lit = h1(seed, x + y) > 0.35;
    rect(c, lit ? (h1(seed + 1, x) > 0.5 ? '#ffd890' : '#fff0c0') : '#1e2430', x, y, w, h);
    if (lit) rect(c, 'rgba(255,255,255,0.35)', x + 0.5, y + 0.5, w * 0.4, h * 0.3);
  } else {
    rect(c, '#7fb0d0', x, y, w, h);
    rect(c, 'rgba(255,255,255,0.45)', x + 0.4, y + 0.4, w * 0.35, h - 0.8);
    rect(c, 'rgba(20,40,60,0.25)', x + w * 0.6, y + h * 0.5, w * 0.4, h * 0.5);
  }
}

/** A split air-conditioner box under a window (very Taiwanese), with a drip stain. */
function acUnit(c: Ctx, x: number, y: number): void {
  block(c, x, y, 6, 4, '#d8d8d0', '#eeeee8', '#9a9a92');
  rect(c, 'rgba(0,0,0,0.25)', x + 0.8, y + 1, 4.4, 0.6);
  rect(c, 'rgba(0,0,0,0.25)', x + 0.8, y + 2.2, 4.4, 0.6);
  rect(c, 'rgba(60,50,40,0.18)', x + 2.4, y + 4, 1.2, 5);
}

/** Iron window grille: thin vertical bars over the pane. */
function grille(c: Ctx, x: number, y: number, w: number, h: number): void {
  for (let k = 1.6; k < w; k += 2) rect(c, 'rgba(40,40,48,0.5)', x + k, y, 0.5, h);
}

/** Stainless rooftop water tank on a little frame (every Taiwanese roof has one). */
function tank(c: Ctx, x: number, y: number, s = 1): void {
  rect(c, '#5a5a62', x - 3 * s, y - 2 * s, 6 * s, 2 * s);
  block(c, x - 3.4 * s, y - 10 * s, 6.8 * s, 8 * s, '#b8bcc4', '#e4e8ee', '#7a7e86');
  ellipse(c, '#d0d4da', x, y - 10 * s, 3.4 * s, 1.2 * s);
  rect(c, 'rgba(255,255,255,0.5)', x - 2.2 * s, y - 9 * s, 1 * s, 6 * s);
}

/** Chinese swallow-tail temple roof between `l` and `r`, eave at `y`, `h` tall, in orange tiles. */
function templeRoof(c: Ctx, l: number, r: number, y: number, h: number, tile = '#d8782a', ridge = '#2f6a4a'): void {
  const w = r - l;
  poly(c, shade(tile, -0.35), [l - 4, y - 5, l + w * 0.3, y + 1, r - w * 0.3, y + 1, r + 4, y - 5, r - w * 0.14, y - h, l + w * 0.14, y - h]);
  poly(c, tile, [l - 2.5, y - 5, l + w * 0.3, y - 0.5, r - w * 0.3, y - 0.5, r + 2.5, y - 5, r - w * 0.15, y - h + 1, l + w * 0.15, y - h + 1]);
  for (let i = l + 2; i < r - 1; i += 2.4) rect(c, 'rgba(90,30,10,0.28)', i, y - h + 2, 0.5, h - 4);
  block(c, l + w * 0.13, y - h - 2.2, w * 0.74, 2.8, ridge, shade(ridge, 0.3));
  for (const s of [-1, 1]) {
    const ex = s < 0 ? l + w * 0.13 : r - w * 0.13;
    poly(c, ridge, [ex, y - h - 2.2, ex + s * 4, y - h - 6.5, ex + s * 1.6, y - h + 0.6]);
  }
}

/** Red lantern with a gold band and tassel. */
function lantern(c: Ctx, x: number, y: number, r: number, night: boolean): void {
  if (night) ellipse(c, 'rgba(255,120,60,0.3)', x, y, r * 2.2, r * 2.4);
  rect(c, '#b8862a', x - r * 0.5, y - r * 1.3, r, 0.8);
  ellipse(c, night ? '#ff6a3a' : '#d8402a', x, y, r, r * 1.1);
  ellipse(c, 'rgba(255,220,160,0.35)', x - r * 0.3, y - r * 0.3, r * 0.35, r * 0.5);
  rect(c, '#b8862a', x - r * 0.5, y + r * 0.9, r, 0.8);
  rect(c, '#e2b23a', x - 0.4, y + r * 1.5, 0.8, r);
}

/** A standing character from the street-life wardrobe. */
function lookOf(i: number): CharLook {
  return LOOKS[((i % LOOKS.length) + LOOKS.length) % LOOKS.length];
}
function stand(c: Ctx, look: CharLook, x: number, y: number, facing: Facing, t: number, seed = 0, moving = false, phase = 0): void {
  const pose: Pose = { facing, phase, moving, blink: (t + seed * 1.7) % 4 < 0.12, breath: (t * 0.45 + seed) % 1 };
  drawCharacter(c, look, x, y, pose);
}

// ---------------------------------------------------------------------------
// Zuoying HSR station, apartments, shophouses, towers
// ---------------------------------------------------------------------------

/** THSR Zuoying: long glass hall under a white wave roof, canopy over the doors, orange THSR logo. */
function hsrStation(c: Ctx, d: Decor): void {
  const w = d.w ?? 416, h = d.h ?? 116;
  art(c, 'khHsr', d.x, d.y, w, h, (c, night) => {
    const L = -w / 2 + 4, R = w / 2 - 4;
    shadow(c, 0, 0, w / 2, 4, 0.3);
    // Side wings: pale concrete with narrow windows.
    block(c, L, -62, 70, 62, '#dfe3e6', '#eef1f3', '#a8aeb4');
    block(c, R - 70, -62, 70, 62, '#dfe3e6', '#eef1f3', '#a8aeb4');
    for (let k = 0; k < 3; k++) {
      for (const x of [L + 8 + k * 20, R - 62 + k * 20]) {
        win(c, x, -54, 12, 10, night, k + 1);
        win(c, x, -38, 12, 10, night, k + 5);
      }
    }
    // Main glass hall.
    block(c, L + 70, -76, R - L - 140, 76, '#c7dde9', '#e2eef5', '#8aa6b6');
    const gl = L + 72, gr = R - 72;
    for (let x = gl; x < gr; x += 14) {
      const lit = night ? (h1(x, 3) > 0.25 ? '#ffe3a8' : '#3a4656') : '#9fcbe3';
      rect(c, lit, x + 1, -73, 12, 34);
      if (!night) rect(c, 'rgba(255,255,255,0.45)', x + 2, -72, 4, 32);
      rect(c, '#eef3f6', x, -74, 1, 36);
    }
    rect(c, '#eef3f6', gl, -56, gr - gl, 1);
    // Clock and the big station name.
    ellipse(c, '#1b1d24', L + 96, -64, 7, 7);
    ellipse(c, '#f8f6f0', L + 96, -64, 6, 6);
    line(c, '#1b1d24', 0.9, [L + 96, -64, L + 96, -68.5]);
    line(c, '#1b1d24', 0.9, [L + 96, -64, L + 99.5, -62.5]);
    block(c, -78, -72, 156, 16, night ? '#1b2230' : '#f6f7f8', undefined, '#8a9096');
    block(c, -74, -69, 10, 10, '#f07a1a', '#ffa050', '#b05010');
    rect(c, '#ffffff', -71.6, -66.4, 5.2, 1.2);
    rect(c, '#ffffff', -69.6, -66.4, 1.2, 5.6);
    txt(c, '高鐵左營站', 8, -64.6, 9, night ? '#ffd27a' : '#1f2a44', 900, 'rgba(0,0,0,0.25)');
    txt(c, 'THSR Zuoying Station', 8, -58.2, 3.4, night ? '#ffe9b8' : '#4a5a7a', 700);
    // Canopy over the doors on slim pillars.
    poly(c, '#5a6470', [gl - 10, -40, gr + 10, -40, gr + 6, -34, gl - 6, -34]);
    block(c, gl - 10, -42, gr - gl + 20, 3, '#f0f2f4', '#ffffff', '#9aa0a6');
    for (let x = gl + 4; x < gr; x += 48) block(c, x, -34, 4, 34, '#eceff1', '#ffffff', '#a0a6ac');
    // Doors: sliding glass, lit from inside.
    for (const dx of [-54, -18, 18]) {
      block(c, dx, -30, 36, 30, '#b8d2e0', '#d6e6ee', '#7a96a6');
      rect(c, night ? '#ffe7b0' : '#cfe4ee', dx + 2, -28, 15, 27);
      rect(c, night ? '#ffe7b0' : '#cfe4ee', dx + 19, -28, 15, 27);
      rect(c, 'rgba(255,255,255,0.4)', dx + 3, -27, 3, 25);
      rect(c, '#9aa6b0', dx + 17, -28, 2, 28);
    }
    // Exit signs and the ticket-hall glow.
    for (const dx of [-36, 36]) {
      block(c, dx - 9, -34, 18, 5, '#2fa36a', '#4fc38a', '#1f7a4a');
      txt(c, '出口 EXIT', dx, -31.4, 2.8, '#ffffff', 800);
    }
    // Information pillar and vending by the right wing, bins by the left.
    block(c, R - 90, -24, 8, 24, '#2f4a8a', '#4a6ab0', '#1f3060');
    txt(c, 'i', R - 86, -16, 7, '#ffffff', 900);
    // Wave roof: a sinuous white band over everything, grey underside.
    const top = (x: number) => -92 + Math.sin((x + w / 2) / 56) * 7 + Math.sin((x + w / 2) / 23) * 1.4;
    c.fillStyle = '#8d97a0';
    c.beginPath();
    c.moveTo(L - 10, top(L - 10) + 12);
    for (let x = L - 10; x <= R + 10; x += 6) c.lineTo(x, top(x) + 12);
    for (let x = R + 10; x >= L - 10; x -= 6) c.lineTo(x, top(x) + 5);
    c.closePath();
    c.fill();
    c.fillStyle = night ? '#dfe4e8' : '#fbfcfd';
    c.beginPath();
    c.moveTo(L - 10, top(L - 10) + 6);
    for (let x = L - 10; x <= R + 10; x += 6) c.lineTo(x, top(x) + 6);
    for (let x = R + 10; x >= L - 10; x -= 6) c.lineTo(x, top(x) - 4);
    c.closePath();
    c.fill();
    c.strokeStyle = 'rgba(40,50,60,0.35)';
    c.lineWidth = 0.7;
    c.beginPath();
    for (let x = L - 10; x <= R + 10; x += 6) c.lineTo(x, top(x) - 4);
    c.stroke();
    // Roof struts between the band and the hall.
    for (let x = gl; x <= gr; x += 28) line(c, '#9aa4ac', 1.2, [x, top(x) + 12, x, -74]);
    if (night) {
      for (let x = gl + 7; x < gr; x += 28) ellipse(c, 'rgba(255,240,200,0.22)', x, top(x) + 20, 16, 8);
    }
  });
}

const APT_TILES = ['#d9c9a8', '#b9cfc0', '#e2d5cf', '#cdb89a'];
const SHOP_NAMES = ['海產', '藥局', '牛排', '手機', '鐘錶', '飲料', '檳榔', '小吃', '美髮', '五金', '麵店', '水果'];
const SIGN_COLS: [string, string][] = [['#c8402e', '#fff3b0'], ['#1f4fa8', '#ffffff'], ['#2fa36a', '#ffffff'], ['#f2c230', '#1b1d24'], ['#8a4aa8', '#ffffff'], ['#f07a1a', '#ffffff']];

/** Tiled Taiwanese apartment block with iron grilles, AC units, balconies and an arcade below. */
function apartments(c: Ctx, d: Decor): void {
  const w = d.w ?? 240, h = d.h ?? 108, v = d.variant ?? 0;
  art(c, `khApt${v}:${w}`, d.x, d.y, w, h, (c, night) => {
    const L = -w / 2 + 2, R = w / 2 - 2, tile = APT_TILES[v % APT_TILES.length];
    shadow(c, 0, 0, w / 2, 4, 0.3);
    block(c, L, -h + 14, R - L, h - 14, tile, shade(tile, 0.12), shade(tile, -0.3));
    // Tile grout.
    for (let y = -h + 16; y < -24; y += 3) rect(c, 'rgba(0,0,0,0.06)', L, y, R - L, 0.5);
    for (let x = L; x < R; x += 4) rect(c, 'rgba(0,0,0,0.05)', x, -h + 14, 0.5, h - 40);
    // Floors.
    const floors = Math.floor((h - 44) / 16);
    for (let f = 0; f < floors; f++) {
      const y = -h + 20 + f * 16;
      rect(c, shade(tile, -0.18), L, y + 13, R - L, 1.2);
      for (let x = L + 8; x < R - 10; x += 22) {
        const k = Math.floor((x - L) / 22);
        if ((f + k) % 5 === 2) {
          // Balcony with plants and laundry.
          block(c, x - 2, y + 6, 16, 6, '#c8c0b0', '#e0d8c8', '#8a8070');
          for (let b = 0; b < 5; b++) rect(c, 'rgba(60,60,70,0.5)', x - 1 + b * 3.2, y + 6, 0.6, 5);
          ellipse(c, '#3f8a3a', x + 2, y + 5.5, 2.4, 1.8);
          ellipse(c, '#57a048', x + 10, y + 5, 2, 1.6);
          win(c, x + 1, y, 10, 6, night, f * 7 + k);
        } else {
          win(c, x, y + 1, 12, 9, night, f * 7 + k);
          grille(c, x, y + 1, 12, 9);
          if ((f * 3 + k) % 4 === 1) acUnit(c, x + 3, y + 11);
        }
      }
    }
    // Arcade: dark recess, columns, shop fronts and shutters.
    rect(c, '#2a2a30', L, -24, R - L, 24);
    for (let x = L + 4; x < R - 10; x += 36) {
      const k = Math.floor((x - L) / 36);
      const [bg, fg] = SIGN_COLS[(k + v) % SIGN_COLS.length];
      if (k % 3 === 1) {
        // Rolled-down shutter.
        block(c, x + 6, -22, 26, 22, '#9aa0a4', '#b8bec2', '#6a7074');
        for (let yy = -20; yy < -1; yy += 2.4) rect(c, 'rgba(0,0,0,0.15)', x + 7, yy, 24, 0.6);
      } else {
        rect(c, night ? '#ffe2a8' : '#dfe8ec', x + 6, -22, 26, 22);
        rect(c, 'rgba(255,255,255,0.35)', x + 7, -21, 6, 20);
        rect(c, '#6a6a70', x + 18, -22, 1, 22);
        // Goods glimpsed inside.
        for (let g = 0; g < 4; g++) rect(c, ['#e2493b', '#3a8fd9', '#ffd34e', '#57c278'][g], x + 8 + g * 5.5, -12 + (g % 2) * 3, 3.6, 3);
      }
      block(c, x + 5, -31, 28, 8, bg, shade(bg, 0.2), shade(bg, -0.3));
      txt(c, SHOP_NAMES[(k * 2 + v) % SHOP_NAMES.length], x + 19, -26.8, 5.6, fg, 900, 'rgba(0,0,0,0.3)');
    }
    for (let x = L; x <= R - 4; x += 36) block(c, x, -24, 4, 24, shade(tile, -0.05), shade(tile, 0.1), shade(tile, -0.35));
    rect(c, shade(tile, -0.1), L, -25, R - L, 2);
    // Roof: parapet, tanks, antenna, a tin shed.
    block(c, L - 1, -h + 11, R - L + 2, 4, shade(tile, 0.05), shade(tile, 0.2), shade(tile, -0.3));
    tank(c, L + 22, -h + 11); tank(c, L + 34, -h + 11, 0.8); tank(c, R - 30, -h + 11);
    line(c, '#3a3e44', 0.8, [R - 12, -h + 11, R - 12, -h - 6]);
    line(c, '#3a3e44', 0.8, [R - 16, -h - 4, R - 8, -h - 4]);
    block(c, L + 60, -h + 3, 36, 8, '#6a8aa0', '#86a6ba', '#4a6a80');
    for (let x = L + 62; x < L + 94; x += 3) rect(c, 'rgba(0,0,0,0.15)', x, -h + 3, 0.6, 8);
  });
}

/** Row of arcaded shophouses (騎樓): three storeys, colourful signboards, stacked shop signs, roof clutter. */
function shophouse(c: Ctx, d: Decor): void {
  const w = d.w ?? 240, h = d.h ?? 100, v = d.variant ?? 0;
  art(c, `khShop${v}:${w}`, d.x, d.y, w, h, (c, night) => {
    const L = -w / 2 + 2, R = w / 2 - 2;
    const tiles = ['#e8c9b8', '#cfe0cf', '#ececec', '#dcc08a'];
    shadow(c, 0, 0, w / 2, 4, 0.3);
    // Each house is 48 px wide and a slightly different height/tile.
    const n = Math.round((R - L) / 48);
    const hw = (R - L) / n;
    for (let i = 0; i < n; i++) {
      const x = L + i * hw, tile = tiles[(i + v) % tiles.length], hh = h - 10 - (h1(i, v) * 12);
      block(c, x, -hh, hw, hh - 24, tile, shade(tile, 0.1), shade(tile, -0.3));
      for (let y = -hh + 2; y < -26; y += 3) rect(c, 'rgba(0,0,0,0.05)', x, y, hw, 0.5);
      // Two upper floors of windows with grilles and ACs.
      for (let f = 0; f < 2; f++) {
        const y = -hh + 8 + f * 22;
        rect(c, shade(tile, -0.2), x, y + 17, hw, 1);
        win(c, x + 6, y, 12, 11, night, i * 5 + f);
        grille(c, x + 6, y, 12, 11);
        win(c, x + hw - 18, y, 12, 11, night, i * 5 + f + 2);
        if ((i + f) % 2 === 0) acUnit(c, x + hw - 15, y + 12);
        else rect(c, '#9a5a3a', x + 6, y + 12, 12, 1.4);
      }
      // Parapet, tank or tin shed on the roof.
      block(c, x - 0.5, -hh - 3, hw + 1, 4, shade(tile, 0.05), shade(tile, 0.2), shade(tile, -0.35));
      if (i % 3 === 0) tank(c, x + hw - 10, -hh - 3, 0.9);
      else if (i % 3 === 1) {
        block(c, x + 6, -hh - 9, hw - 14, 7, '#7a8a96', '#98a8b4', '#56666e');
        for (let xx = x + 7; xx < x + hw - 9; xx += 3) rect(c, 'rgba(0,0,0,0.15)', xx, -hh - 9, 0.6, 7);
      } else {
        line(c, '#3a3e44', 0.8, [x + hw / 2, -hh - 3, x + hw / 2, -hh - 14]);
        for (const dy of [-12, -9]) line(c, '#3a3e44', 0.7, [x + hw / 2 - 4, -hh + dy, x + hw / 2 + 4, -hh + dy]);
      }
      // Signboard over the arcade.
      const [bg, fg] = SIGN_COLS[(i * 2 + v) % SIGN_COLS.length];
      block(c, x + 2, -34, hw - 4, 10, bg, shade(bg, 0.2), shade(bg, -0.3));
      const name = SHOP_NAMES[(i * 3 + v * 2) % SHOP_NAMES.length];
      txt(c, name, x + hw / 2, -28.6, 6.4, fg, 900, 'rgba(0,0,0,0.3)');
      if (night) {
        ellipse(c, `rgba(255,220,160,0.12)`, x + hw / 2, -29, hw / 2, 7);
        txt(c, name, x + hw / 2, -28.6, 6.4, '#ffffff', 900);
      }
      // Arcade recess and shop front.
      rect(c, '#26262c', x + 3, -24, hw - 6, 24);
      if (i % 4 === 3) {
        block(c, x + 8, -22, hw - 16, 22, '#9aa0a4', '#b8bec2', '#6a7074');
        for (let yy = -20; yy < -1; yy += 2.4) rect(c, 'rgba(0,0,0,0.15)', x + 9, yy, hw - 18, 0.6);
      } else {
        rect(c, night ? '#ffe2a8' : '#dfe8ec', x + 8, -22, hw - 16, 22);
        rect(c, 'rgba(255,255,255,0.35)', x + 9, -21, 5, 20);
        rect(c, '#6a6a70', x + hw / 2, -22, 1, 22);
        for (let g = 0; g < 3; g++) rect(c, ['#e2493b', '#3a8fd9', '#ffd34e', '#57c278', '#ff9f43'][(g + i) % 5], x + 10 + g * 7, -12 + (g % 2) * 3, 4.6, 3.2);
        if (night) ellipse(c, 'rgba(255,230,180,0.18)', x + hw / 2, 0, hw / 2, 6);
      }
      // Arcade columns.
      block(c, x, -24, 4, 24, shade(tile, -0.05), shade(tile, 0.1), shade(tile, -0.35));
    }
    block(c, R - 4, -24, 4, 24, '#d8d0c0', '#e8e0d0', '#8a8070');
    rect(c, 'rgba(0,0,0,0.2)', L, -25, R - L, 1.6);
  });
}

/** Three slim residential towers (new Kaohsiung), window grids, rooftop tanks, a red aviation light. */
function towers(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 208, h = d.h ?? 150;
  art(c, 'khTowers', d.x, d.y, w, h, (c, night) => {
    shadow(c, 0, 0, w / 2, 4, 0.3);
    const specs: [number, number, number, string][] = [[-w / 2 + 6, 60, h - 30, '#d8dde2'], [-26, 60, h, '#e6e0d6'], [w / 2 - 70, 64, h - 50, '#cfd8dc']];
    for (const [x, tw, th, col] of specs) {
      block(c, x, -th, tw, th, col, shade(col, 0.1), shade(col, -0.3));
      for (let y = -th + 8; y < -18; y += 10) {
        for (let xx = x + 5; xx < x + tw - 8; xx += 9) win(c, xx, y, 6, 6, night, Math.round(y + xx));
        rect(c, shade(col, -0.15), x, y + 8, tw, 0.8);
      }
      rect(c, '#2a2a30', x + 2, -14, tw - 4, 14);
      rect(c, night ? '#ffe4a8' : '#cfe0ea', x + 4, -12, tw - 8, 12);
      block(c, x - 1, -th - 3, tw + 2, 4, shade(col, 0.05), shade(col, 0.2), shade(col, -0.3));
      tank(c, x + 10, -th - 3, 0.9); tank(c, x + tw - 10, -th - 3, 0.9);
    }
  });
  // The aviation light on the tallest tower blinks (drawn live).
  if (isNight() && Math.sin(t * 2.4) > 0.3) ellipse(c, 'rgba(255,60,60,0.9)', d.x + 4, d.y - (d.h ?? 150) - 4, 1.6, 1.6);
}

/** Harbour-side low rise: a port office with a blue stripe, or a corrugated shed. */
function lowRise(c: Ctx, d: Decor): void {
  const w = d.w ?? 80, h = d.h ?? 70, v = d.variant ?? 0;
  art(c, `khLow${v}:${w}`, d.x, d.y, w, h, (c, night) => {
    const L = -w / 2 + 2, R = w / 2 - 2;
    shadow(c, 0, 0, w / 2, 3, 0.3);
    if (v === 0) {
      block(c, L, -h + 8, R - L, h - 8, '#e8ecee', '#f6f8f9', '#9aa4aa');
      rect(c, '#1f5fa8', L, -h + 20, R - L, 4);
      for (let f = 0; f < 3; f++) for (let x = L + 6; x < R - 8; x += 14) win(c, x, -h + 28 + f * 14, 9, 8, night, f * 3 + x);
      block(c, -10, -16, 20, 16, '#b8d2e0', '#d6e6ee', '#7a96a6');
      txt(c, '港務公司', 0, -h + 14, 4.6, '#1f3a6e', 900);
      block(c, L - 1, -h + 5, R - L + 2, 4, '#dfe3e6', '#f0f3f5', '#8a9096');
      tank(c, R - 10, -h + 5, 0.8);
    } else {
      block(c, L, -h + 16, R - L, h - 16, '#8aa0b0', '#a4b8c6', '#5a7080');
      for (let x = L + 2; x < R - 1; x += 3) rect(c, 'rgba(0,0,0,0.12)', x, -h + 16, 0.6, h - 16);
      poly(c, '#6a8090', [L - 2, -h + 17, 0, -h, R + 2, -h + 17]);
      poly(c, '#9ab0be', [L + 2, -h + 17, 0, -h + 4, R - 2, -h + 17]);
      block(c, -8, -18, 16, 18, '#4a5a66', '#6a7a86', '#2a3a46');
    }
  });
}

// ---------------------------------------------------------------------------
// Lotus Pond: pagodas, pavilions, the statue, causeway rails, glints
// ---------------------------------------------------------------------------

/** Dragon (0) or Tiger (1) Pagoda: seven orange-roofed tiers on red columns, the beast's mouth as the door. */
function pagoda(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, h = d.h ?? 124, v = d.variant ?? 0;
  art(c, `khPagoda${v}`, d.x, d.y, w, h, (c, night) => {
    shadow(c, 0, 0, w / 2 - 2, 4, 0.3);
    // Platform.
    block(c, -w / 2 + 2, -8, w - 4, 8, '#e8d8c8', '#f4ece0', '#a89888');
    rect(c, '#c8402e', -w / 2 + 2, -9, w - 4, 1.6);
    const tiers = 7;
    for (let k = 0; k < tiers; k++) {
      const tw = (w - 10) * (1 - k * 0.09);
      const y = -10 - k * 15;
      // Walls: cream with red columns and a dark window.
      block(c, -tw / 2 + 3, y - 10, tw - 6, 10, '#f4e8d4', '#fff8ea', '#b8a080');
      for (let x = -tw / 2 + 5; x < tw / 2 - 2; x += 8) rect(c, '#c8402e', x, y - 10, 2, 10);
      rect(c, night ? '#ffd27a' : '#4a3a30', -2.4, y - 8, 4.8, 6);
      // Balustrade.
      rect(c, '#e8e0d0', -tw / 2 + 2, y - 1, tw - 4, 1.2);
      // Roof with upturned corners.
      templeRoof(c, -tw / 2 + 1, tw / 2 - 1, y - 10, 6, k % 2 ? '#e88a2a' : '#f0a030', '#2f6a4a');
    }
    // Finial.
    const top = -10 - tiers * 15 - 8;
    poly(c, '#f2c230', [-3, top + 2, 3, top + 2, 0, top - 7]);
    ellipse(c, '#f2c230', 0, top - 8, 1.6, 1.6);
    // The beast at the entrance, mouth open towards the bridge.
    if (v === 0) {
      // Dragon: green scales, gold mane, red tongue.
      ellipse(c, '#2f8a5a', 0, -10, 15, 8);
      ellipse(c, '#3fa86a', 0, -12, 13, 6.5);
      for (let x = -11; x <= 11; x += 4) ellipse(c, '#f2c230', x, -18 + Math.abs(x) * 0.3, 1.8, 2.6);
      ellipse(c, '#1b1d24', -6, -13, 1.6, 1.6); ellipse(c, '#1b1d24', 6, -13, 1.6, 1.6);
      ellipse(c, '#ffffff', -6.4, -13.4, 0.6, 0.6); ellipse(c, '#ffffff', 5.6, -13.4, 0.6, 0.6);
      poly(c, '#e2493b', [-9, -9, 9, -9, 7, -2, -7, -2]);
      poly(c, '#1b1d24', [-7.5, -8, 7.5, -8, 6, -2.5, -6, -2.5]);
      for (let x = -6; x <= 6; x += 3) poly(c, '#ffffff', [x - 1, -8, x + 1, -8, x, -5.6]);
      poly(c, '#e2493b', [-3, -2.5, 3, -2.5, 0, 0.5]);
      line(c, '#f2c230', 1, [-14, -12, -19, -16, -18, -20]);
      line(c, '#f2c230', 1, [14, -12, 19, -16, 18, -20]);
    } else {
      // Tiger: orange with black stripes, white cheeks.
      ellipse(c, '#e8902a', 0, -10, 15, 8);
      ellipse(c, '#f4a83a', 0, -12, 13, 6.5);
      for (const x of [-9, -3, 3, 9]) rect(c, '#1b1d24', x - 1, -19, 2, 5);
      ellipse(c, '#f8f4ea', -7, -9, 4, 3); ellipse(c, '#f8f4ea', 7, -9, 4, 3);
      ellipse(c, '#1b1d24', -6, -13, 1.6, 1.6); ellipse(c, '#1b1d24', 6, -13, 1.6, 1.6);
      ellipse(c, '#ffffff', -6.4, -13.4, 0.6, 0.6); ellipse(c, '#ffffff', 5.6, -13.4, 0.6, 0.6);
      ellipse(c, '#e8902a', -12, -18, 3, 3); ellipse(c, '#e8902a', 12, -18, 3, 3);
      poly(c, '#1b1d24', [-7, -8, 7, -8, 5.5, -2.5, -5.5, -2.5]);
      for (let x = -5; x <= 5; x += 2.5) poly(c, '#ffffff', [x - 0.9, -8, x + 0.9, -8, x, -5.4]);
      poly(c, '#e2493b', [-3, -2.5, 3, -2.5, 0, 0.5]);
      txt(c, '王', 0, -17.6, 3.2, '#1b1d24', 900);
    }
    if (night) {
      ellipse(c, 'rgba(255,190,90,0.22)', 0, -60, w / 2 + 6, 60);
      for (let k = 0; k < tiers; k++) rect(c, 'rgba(255,220,140,0.35)', -(w - 10) * (1 - k * 0.09) / 2 + 2, -10 - k * 15 - 1, (w - 10) * (1 - k * 0.09) - 4, 1.2);
    }
  });
}

/** Red zig-zag railings along the causeway out to the pagodas (two tiles wide, `h` tall). */
function causeway(c: Ctx, d: Decor): void {
  const w = d.w ?? 32, h = d.h ?? 112, L = d.x - w / 2, R = d.x + w / 2, top = d.y - h;
  for (const x of [L + 1.5, R - 1.5]) {
    rect(c, '#c8402e', x - 0.8, top, 1.6, h);
    rect(c, '#e8604a', x - 0.8, top, 0.6, h);
    for (let y = top; y <= d.y; y += 8) {
      block(c, x - 1.4, y - 3, 2.8, 4, '#c8402e', '#e8604a', '#8a2a1e');
      rect(c, '#f2c230', x - 0.7, y - 4.6, 1.4, 1.6);
    }
  }
  // Lotus-bud lamp posts on the corners.
  for (const x of [L + 1.5, R - 1.5]) {
    rect(c, '#7a6a5a', x - 0.8, d.y - 22, 1.6, 22);
    ellipse(c, isNight() ? '#ffd27a' : '#f8e8c8', x, d.y - 24, 2.4, 3);
  }
}

/** Spring and Autumn Pavilions: twin octagonal towers on a terrace, Guanyin on her dragon between them. */
function pavilion(c: Ctx, d: Decor): void {
  const w = d.w ?? 72, h = d.h ?? 96;
  art(c, 'khPav', d.x, d.y, w, h, (c, night) => {
    shadow(c, 0, 0, w / 2 - 2, 4, 0.3);
    block(c, -w / 2 + 2, -8, w - 4, 8, '#e8d8c8', '#f4ece0', '#a89888');
    for (const cx of [-18, 18]) {
      for (let k = 0; k < 4; k++) {
        const tw = 26 - k * 3.5, y = -8 - k * 17;
        block(c, cx - tw / 2 + 2, y - 11, tw - 4, 11, '#f4e8d4', '#fff8ea', '#b8a080');
        for (let x = cx - tw / 2 + 3.5; x < cx + tw / 2 - 2; x += 6) rect(c, '#c8402e', x, y - 11, 1.8, 11);
        rect(c, night ? '#ffd27a' : '#4a3a30', cx - 2, y - 9, 4, 5);
        templeRoof(c, cx - tw / 2, cx + tw / 2, y - 11, 6, k % 2 ? '#e88a2a' : '#f0a030', '#2f6a4a');
      }
      poly(c, '#f2c230', [cx - 2.4, -76, cx + 2.4, -76, cx, -83]);
    }
    // Guanyin riding the dragon between the towers.
    ellipse(c, '#2f8a5a', 0, -14, 10, 4);
    line(c, '#3fa86a', 2.2, [-10, -14, -14, -20, -12, -26]);
    ellipse(c, '#f2c230', -12, -27, 2, 2);
    block(c, -3.5, -34, 7, 18, '#f4f1ea', '#ffffff', '#b8b0a0');
    ellipse(c, '#f0c8a4', 0, -37, 2.8, 3);
    ellipse(c, '#1b1d24', 0, -39.5, 3, 1.6);
    ellipse(c, 'rgba(255,220,120,0.4)', 0, -36, 6, 6);
    if (night) ellipse(c, 'rgba(255,190,90,0.22)', 0, -45, w / 2 + 4, 46);
  });
}

/** The 72 m Xuantian Shangdi (北極玄天上帝) statue: black-robed god with sword and snake-turtle, on a shrine. */
function xuantian(c: Ctx, d: Decor): void {
  const w = d.w ?? 80, h = d.h ?? 140;
  art(c, 'khXuan', d.x, d.y, w, h, (c, night) => {
    shadow(c, 0, 0, w / 2 - 2, 4, 0.3);
    // Shrine hall under his feet: red walls, golden roof.
    block(c, -w / 2 + 4, -26, w - 8, 26, '#c8402e', '#e05a48', '#8a2a1e');
    for (let x = -w / 2 + 8; x < w / 2 - 8; x += 12) rect(c, night ? '#ffd27a' : '#2a1810', x, -20, 5, 12);
    templeRoof(c, -w / 2 + 2, w / 2 - 2, -26, 8, '#f0a030', '#2f6a4a');
    // Pedestal with the snake-turtle.
    block(c, -16, -44, 32, 10, '#6a6a62', '#8a8a82', '#3a3a34');
    ellipse(c, '#3f8a5a', -8, -46, 7, 3.5);
    ellipse(c, '#2a2a30', 7, -46, 5, 2.6);
    line(c, '#3f8a5a', 1.6, [-15, -46, -19, -50]);
    // Robe: black with gold trim, wide sleeves.
    poly(c, '#1b1d24', [-18, -46, 18, -46, 13, -92, -13, -92]);
    poly(c, '#2a2f3d', [-16, -47, 16, -47, 11, -90, -11, -90]);
    rect(c, '#f2c230', -14, -50, 28, 1.6);
    rect(c, '#f2c230', -1, -90, 2, 42);
    poly(c, '#1b1d24', [-13, -84, -26, -70, -22, -64, -11, -72]);
    poly(c, '#1b1d24', [13, -84, 26, -70, 22, -64, 11, -72]);
    // Hands: one on the sword hilt, one raised in blessing.
    ellipse(c, '#f0c8a4', -23, -66, 2.6, 2.6);
    ellipse(c, '#f0c8a4', 23, -72, 2.6, 2.6);
    rect(c, '#d8d8e0', -24, -108, 2, 42);
    poly(c, '#f2c230', [-27, -68, -21, -68, -24, -62]);
    // Head: long black beard, crown.
    rect(c, '#1b1d24', -5, -96, 10, 10);
    ellipse(c, '#f0c8a4', 0, -102, 6, 7);
    ellipse(c, '#1b1d24', 0, -94, 4.5, 5);
    rect(c, '#1b1d24', -2.4, -101, 1.4, 1.2); rect(c, '#1b1d24', 1, -101, 1.4, 1.2);
    block(c, -7, -114, 14, 7, '#f2c230', '#ffe070', '#b08010');
    poly(c, '#f2c230', [-7, -114, 7, -114, 0, -122]);
    ellipse(c, '#e2493b', 0, -112, 1.6, 1.6);
    if (night) {
      ellipse(c, 'rgba(200,210,255,0.22)', 0, -80, 40, 60);
      ellipse(c, 'rgba(255,200,120,0.3)', 0, -16, w / 2, 14);
    }
  });
}

/** Shore railing: white stone balustrade (0) or green metal rail (1), `w` wide. */
function railing(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, x0 = d.x, y = d.y, v = d.variant ?? 0;
  if (v === 0) {
    rect(c, '#ece6d8', x0, y - 9, w, 2);
    rect(c, 'rgba(0,0,0,0.15)', x0, y - 7.2, w, 0.8);
    for (let x = x0 + 2; x < x0 + w; x += 7) {
      block(c, x, y - 7, 2.6, 7, '#e8e0d0', '#f6f0e4', '#a89888');
      ellipse(c, '#e8e0d0', x + 1.3, y - 4, 1.8, 1.4);
    }
    rect(c, '#d8d0c0', x0, y - 1, w, 1.4);
  } else {
    rect(c, '#2f6a4a', x0, y - 10, w, 1.6);
    rect(c, '#2f6a4a', x0, y - 6, w, 1);
    for (let x = x0 + 2; x < x0 + w; x += 10) rect(c, '#2f6a4a', x, y - 10, 1.4, 10);
  }
}

/** A small paifang gate on the shore walk: red pillars, green beam, gold 蓮池潭. */
function templeGate(c: Ctx, d: Decor): void {
  const w = d.w ?? 72, x = d.x, y = d.y;
  art(c, 'khGate', x, y, w, 60, (c, night) => {
    shadow(c, 0, 0, w / 2, 3, 0.3);
    for (const px of [-w / 2 + 6, w / 2 - 10]) {
      block(c, px - 2, -6, 8, 6, '#d8d0c0', '#ece6d8', '#8a8070');
      block(c, px, -42, 4, 36, '#c8402e', '#e05a48', '#8a2a1e');
    }
    block(c, -w / 2 + 2, -46, w - 4, 7, '#2f6a4a', '#4a8a6a', '#1f4a30');
    txt(c, '蓮 池 潭', 0, -42.4, 5.4, '#f2c230', 900, 'rgba(0,0,0,0.35)');
    templeRoof(c, -w / 2 - 2, w / 2 + 2, -47, 7, '#f0a030', '#2f6a4a');
    txt(c, 'LOTUS POND', 0, -36.6, 2.6, '#f4f1ea', 700);
    for (const px of [-w / 2 + 14, w / 2 - 14]) lantern(c, px, -34, 2.6, night);
  });
}

/** Bronze incense burner with three legs and a curl of smoke. */
function incense(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 7, 2, 0.3);
  for (const dx of [-5, 0, 5]) rect(c, '#5a4a30', x + dx - 1, y - 5, 2, 5);
  block(c, x - 8, y - 16, 16, 11, '#7a6a3a', '#9a8a5a', '#4a3a20');
  ellipse(c, '#6a5a2a', x, y - 16, 8, 2.4);
  rect(c, '#f2c230', x - 6, y - 12, 12, 0.8);
  for (const dx of [-7, 7]) block(c, x + dx - 1.6, y - 22, 3.2, 7, '#7a6a3a', '#9a8a5a', '#4a3a20');
  for (let i = 0; i < 3; i++) rect(c, '#c8402e', x - 3 + i * 3, y - 24, 0.8, 8);
  steam(c, x, y - 24, t, 4, 0.3);
}

/** Sun glints on the water (a wide, flat, animated layer); dimmer at night. */
function glints(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 160, h = d.h ?? 48, L = d.x - w / 2, top = d.y - h, v = d.variant ?? 0;
  const night = isNight();
  for (let i = 0; i < 16; i++) {
    const sx = L + h1(i, 11 + v) * w + ((t * (6 + h1(i, 12) * 8)) % 30) - 15;
    const sy = top + h1(i, 13 + v) * h;
    const sp = Math.sin(t * 2.2 + i * 1.3 + v) * 0.5 + 0.5;
    if (sp < 0.45) continue;
    const a = (sp - 0.45) * (night ? 0.8 : 1.5);
    ellipse(c, `rgba(255,255,245,${Math.min(0.85, a)})`, sx, sy, 2.6 + sp * 2, 0.7);
  }
}

// ---------------------------------------------------------------------------
// Streets: signs, posts, lane paint, stops, stores, temples, kiosks
// ---------------------------------------------------------------------------

/** "TAXI 計程車招呼站" post. */
function taxiSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 3, 1.2);
  rect(c, '#5a6470', x - 0.9, y - 30, 1.8, 30);
  block(c, x - 9, y - 40, 18, 11, '#f2c230', '#ffe070', '#b08010');
  txt(c, 'TAXI', x, y - 36.6, 5, '#1b1d24', 900);
  txt(c, '計程車招呼站', x, y - 31.8, 2.6, '#1b1d24', 800);
}

const SIGNPOSTS: [string, string, number][][] = [
  [['捷運 MRT', '#1f4fa8', 1], ['蓮池潭 Lotus Pond', '#2f6a4a', 1], ['計程車 Taxi', '#f2c230', -1]],
  [['龍虎塔 Pagodas', '#c8402e', 1], ['孔廟 Confucius Temple', '#2f6a4a', -1], ['高鐵站 THSR', '#1f4fa8', -1]],
  [['駁二 Pier-2', '#8a4aa8', 1], ['哈瑪星 Hamasen', '#2f6a4a', -1], ['渡輪 Ferry', '#1f4fa8', -1]],
  [['旗津渡輪 Cijin Ferry', '#1f4fa8', -1], ['85大樓 Sky Tower', '#c8402e', 1], ['駁二 Pier-2', '#8a4aa8', -1]],
];

/** Direction post with three coloured arrow boards (`variant` picks the set). */
function signpost(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = (d.variant ?? 0) % SIGNPOSTS.length;
  art(c, `khPost${v}`, x, y, 72, 44, (c) => {
    shadow(c, 0, 0, 3, 1.2);
    rect(c, '#5a6470', -0.9, -40, 1.8, 40);
    SIGNPOSTS[v].forEach(([text, col, dir], i) => {
      const yy = -38 + i * 8;
      const tw = 30, l = dir > 0 ? 1 : -1 - tw;
      poly(c, col, dir > 0 ? [l, yy, l + tw - 3, yy, l + tw, yy + 3.2, l + tw - 3, yy + 6.4, l, yy + 6.4] : [l + 3, yy, l + tw, yy, l + tw, yy + 6.4, l + 3, yy + 6.4, l, yy + 3.2]);
      txt(c, text, l + tw / 2, yy + 3.4, 2.6, col === '#f2c230' ? '#1b1d24' : '#ffffff', 800);
    });
  }, false);
}

const STREETS: [string, string, string][] = [['中山路', 'Zhongshan Rd', '#1f6a3a'], ['五福路', 'Wufu Rd', '#1f6a3a'], ['愛河', 'Love River', '#7a4a2a']];

/** Street-name plate (green) or tourist sign (brown) on a post. */
function streetSign(c: Ctx, d: Decor): void {
  const [zh, en, col] = STREETS[(d.variant ?? 0) % STREETS.length];
  const x = d.x, y = d.y;
  shadow(c, x, y, 3, 1.2);
  rect(c, '#5a6470', x - 0.9, y - 26, 1.8, 26);
  block(c, x - 14, y - 34, 28, 9, col, shade(col, 0.2), shade(col, -0.3));
  txt(c, zh, x, y - 31.4, 4.6, '#ffffff', 900);
  txt(c, en, x, y - 27.4, 2.2, '#ffffff', 700);
}

/** White lane arrow painted on the road: 0 up, 1 down, 2 left, 3 right. */
function laneArrow(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y - 6, v = d.variant ?? 0;
  const col = 'rgba(255,255,255,0.78)';
  if (v < 2) {
    const s = v === 0 ? -1 : 1;
    rect(c, col, x - 1.2, y - 5, 2.4, 10);
    poly(c, col, [x - 4, y + s * 4, x + 4, y + s * 4, x, y + s * 9]);
  } else {
    const s = v === 2 ? -1 : 1;
    rect(c, col, x - 6, y - 1.2, 12, 2.4);
    poly(c, col, [x + s * 5, y - 4, x + s * 5, y + 4, x + s * 10, y]);
  }
}

/** Bridge railing over the river for the road bridges, with heart motifs (Love River!). */
function bridgeRail(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y, night = isNight();
  rect(c, '#dfe3e6', L, y - 8, w, 1.6);
  for (let x = L + 1; x < L + w; x += 8) rect(c, '#dfe3e6', x, y - 8, 1.2, 8);
  rect(c, '#dfe3e6', L, y - 1.2, w, 1.4);
  for (let x = L + 5; x < L + w - 2; x += 16) {
    const col = night ? '#ff6aa0' : '#e05a8a';
    ellipse(c, col, x - 1.2, y - 5.4, 1.4, 1.4);
    ellipse(c, col, x + 1.2, y - 5.4, 1.4, 1.4);
    poly(c, col, [x - 2.5, y - 4.8, x + 2.5, y - 4.8, x, y - 2]);
    if (night) ellipse(c, 'rgba(255,120,170,0.3)', x, y - 4.5, 4, 4);
  }
}

/** Bus shelter with a bench, a route board and an ad at the back. */
function busStop(c: Ctx, d: Decor): void {
  const w = d.w ?? 48, x = d.x, y = d.y, v = d.variant ?? 0;
  art(c, `khBus${v}`, x, y, w, 40, (c, night) => {
    shadow(c, 0, 0, w / 2, 3, 0.3);
    for (const px of [-w / 2 + 2, w / 2 - 4]) rect(c, '#5a6470', px, -30, 2, 30);
    block(c, -w / 2 + 2, -16, 12, 16, '#c8d8e0', '#e8f0f4', '#7a8a96');
    block(c, -w / 2 + 3, -14, 10, 11, v ? '#f07a1a' : '#2fa36a', undefined, '#1b1d24');
    txt(c, v ? '美麗島' : '捷運', -w / 2 + 8, -8.4, 3, '#ffffff', 900);
    block(c, -w / 2 + 16, -8, w - 20, 2.5, '#9a6a40', '#b8885a', '#6a4a2a');
    for (const px of [-w / 2 + 18, w / 2 - 7]) rect(c, '#3a3e44', px, -5.5, 1.4, 5.5);
    block(c, -w / 2, -34, w, 5, '#e8ecef', '#ffffff', '#8a9096');
    block(c, 2, -30, 12, 13, '#f8e8c0', undefined, '#6a6060');
    txt(c, v ? '高雄' : '公車', 8, -23.4, 4, '#c8402e', 900);
    if (night) ellipse(c, 'rgba(255,240,200,0.2)', 0, -20, w / 2, 14);
  });
}

/** Convenience store front (Hi-Mart style stripes); lit all night, an ice-cream flag by the door. */
function store(c: Ctx, d: Decor): void {
  const w = d.w ?? 96, x = d.x, y = d.y, v = d.variant ?? 0;
  art(c, `khStore${v}`, x, y, w, 54, (c, night) => {
    const L = -w / 2 + 2, R = w / 2 - 2;
    shadow(c, 0, 0, w / 2, 3, 0.3);
    block(c, L, -36, R - L, 36, '#f4f4f0', '#ffffff', '#a8a8a0');
    // Glass front.
    rect(c, night ? '#fff0c8' : '#cfe4ee', L + 4, -26, R - L - 8, 26);
    for (let xx = L + 8; xx < R - 10; xx += 10) {
      for (let s = 0; s < 3; s++) rect(c, ['#e2493b', '#3a8fd9', '#ffd34e', '#57c278', '#ff9f43', '#9b5fc0'][(s + Math.round(xx)) % 6], xx, -20 + s * 6, 6, 3.4);
    }
    rect(c, 'rgba(255,255,255,0.35)', L + 6, -24, 8, 22);
    rect(c, '#8a9096', 0, -26, 1.2, 26);
    // Stripe fascia.
    const stripes = v ? ['#1f6fb0', '#2fa36a', '#ffffff'] : ['#f07a1a', '#2fa36a', '#c8402e'];
    stripes.forEach((col, i) => rect(c, col, L, -36 + i * 3.4, R - L, 3.4));
    block(c, -22, -46, 44, 11, '#ffffff', undefined, '#8a9096');
    txt(c, v ? 'OK·超商' : 'Hi·Mart', 0, -40.6, 6, v ? '#1f6fb0' : '#f07a1a', 900);
    // Ice-cream flag and a drinks cooler by the door.
    rect(c, '#9aa0a6', R - 10, -30, 1, 30);
    poly(c, '#ff9fc0', [R - 9, -30, R, -26, R - 9, -22]);
    block(c, L + 6, -12, 10, 12, '#2f6fb0', '#4a8ad0', '#1f4a80');
    if (night) ellipse(c, 'rgba(255,240,200,0.25)', 0, -8, w / 2, 10);
  });
}

/** Small neighbourhood temple front: red columns, swallow-tail roof, lanterns, incense table. */
function temple(c: Ctx, d: Decor): void {
  const w = d.w ?? 60, x = d.x, y = d.y, v = d.variant ?? 0;
  art(c, `khTemple${v}`, x, y, w, 60, (c, night) => {
    shadow(c, 0, 0, w / 2, 3, 0.3);
    block(c, -w / 2 + 2, -34, w - 4, 34, '#c8402e', '#e05a48', '#8a2a1e');
    rect(c, night ? '#ffd27a' : '#2a1810', -10, -26, 20, 26);
    for (let k = 0; k < 3; k++) rect(c, '#f2c230', -8 + k * 7, -22, 1.2, 18);
    for (const px of [-w / 2 + 4, w / 2 - 8]) block(c, px, -34, 4, 34, '#a8301e', '#c84a36', '#6a1a10');
    block(c, -16, -40, 32, 7, '#2f6a4a', '#4a8a6a', '#1f4a30');
    txt(c, v ? '福德宮' : '天后宮', 0, -36.4, 5, '#f2c230', 900, 'rgba(0,0,0,0.35)');
    templeRoof(c, -w / 2, w / 2, -41, 9, '#e88a2a', '#2f6a4a');
    for (const px of [-w / 2 + 12, w / 2 - 12]) lantern(c, px, -28, 3, night);
    block(c, -8, -8, 16, 8, '#7a6a3a', '#9a8a5a', '#4a3a20');
    for (let i = 0; i < 3; i++) rect(c, '#c8402e', -3 + i * 3, -14, 0.8, 6);
  });
  steam(c, x, y - 14, 0, 2, 0.2);
}

/** Bubble tea kiosk. */
function teaKiosk(c: Ctx, d: Decor, t: number): void {
  const r: Rect = { x: d.x - 14, y: d.y - 10, w: 28, h: 10 };
  stall(c, r, t, '珍珠奶茶', '#8a5a3a', (c, r) => {
    for (let k = 0; k < 4; k++) {
      block(c, r.x + 3 + k * 6, r.y - 14, 4.4, 7, ['#d8b890', '#f4b6c6', '#b8e0c0', '#f0d8a0'][k], undefined, '#6a5a4a');
      rect(c, '#1b1d24', r.x + 5 + k * 6, r.y - 18, 0.8, 5);
    }
  });
}

/** Park pavilion: hexagonal orange roof on red columns, benches inside. */
function pavilionSmall(c: Ctx, d: Decor): void {
  const w = d.w ?? 60, x = d.x, y = d.y;
  art(c, 'khPavS', x, y, w, 56, (c, night) => {
    shadow(c, 0, 0, w / 2, 3, 0.3);
    block(c, -w / 2 + 4, -4, w - 8, 4, '#d8d0c0', '#ece6d8', '#8a8070');
    for (const px of [-w / 2 + 6, -4, w / 2 - 10]) block(c, px, -30, 4, 26, '#c8402e', '#e05a48', '#8a2a1e');
    block(c, -w / 2 + 10, -12, w - 20, 3, '#9a6a40', '#b8885a', '#6a4a2a');
    templeRoof(c, -w / 2, w / 2, -30, 12, '#e88a2a', '#2f6a4a');
    poly(c, '#f2c230', [-2, -42, 2, -42, 0, -48]);
    if (night) ellipse(c, 'rgba(255,220,160,0.18)', 0, -16, w / 2 - 4, 10);
  });
}

/** Outdoor exercise machine (blue and yellow), the kind every Taiwanese park has. */
function exercise(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 7, 1.8);
  rect(c, '#2f6fb0', x - 6, y - 22, 2, 22);
  rect(c, '#2f6fb0', x + 4, y - 22, 2, 22);
  rect(c, '#2f6fb0', x - 6, y - 22, 12, 2);
  const sw = Math.sin(t * 1.4) * 3;
  line(c, '#f2c230', 1.6, [x - 5, y - 21, x - 5 + sw, y - 8]);
  line(c, '#f2c230', 1.6, [x + 5, y - 21, x + 5 - sw, y - 8]);
  block(c, x - 7 + sw, y - 8, 4, 2, '#1b1d24');
  block(c, x + 3 - sw, y - 8, 4, 2, '#1b1d24');
}

/** Elders doing tai chi in the morning (6–9): three slow figures turning together. */
function taichi(c: Ctx, d: Decor, t: number): void {
  const tm = now();
  if (tm < 6 * 60 || tm >= 9 * 60) return;
  const v = d.variant ?? 0, w = d.w ?? 48;
  const facings: Facing[] = ['down', 'left', 'down', 'right'];
  for (let i = 0; i < 3; i++) {
    const x = d.x - w / 2 + 8 + i * (w / 2 - 4), y = d.y + (i % 2) * 6;
    const look = lookOf(3 + 8 * ((i + v) % 4));
    const ph = (t * 0.25 + i * 0.1) % 1;
    stand(c, look, x, y, facings[Math.floor(((t * 0.08 + v) % 1) * 4)], t, i, true, ph);
  }
}

const ART_COLS = ['#e2493b', '#3a8fd9', '#ffd34e', '#57c278', '#ff9f43', '#9b5fc0'];

/** Public art: 0 steel ring, 1 colour cubes, 2 LOVE letters, 3 leaping fish. */
function sculpture(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  shadow(c, x, y, 9, 2.4, 0.3);
  block(c, x - 9, y - 4, 18, 4, '#9a9690', '#b8b4ac', '#6a6660');
  if (v === 0) {
    c.strokeStyle = '#c8ccd0';
    c.lineWidth = 3;
    c.beginPath();
    c.ellipse(x, y - 17, 10, 12, 0.3, 0, Math.PI * 2);
    c.stroke();
    c.strokeStyle = 'rgba(255,255,255,0.6)';
    c.lineWidth = 1;
    c.beginPath();
    c.ellipse(x - 1, y - 18, 9, 11, 0.3, Math.PI, Math.PI * 1.7);
    c.stroke();
  } else if (v === 1) {
    const cubes: [number, number, number][] = [[-6, -4, 0], [2, -4, 1], [-2, -11, 2], [4, -16, 3]];
    for (const [dx, dy, k] of cubes) block(c, x + dx, y + dy - 7, 8, 7, ART_COLS[k], shade(ART_COLS[k], 0.3), shade(ART_COLS[k], -0.3));
  } else if (v === 2) {
    txt(c, 'LOVE', x, y - 11, 11, '#e2493b', 900, 'rgba(0,0,0,0.35)');
    ellipse(c, '#ff6aa0', x + 10, y - 20, 2, 2); ellipse(c, '#ff6aa0', x + 13, y - 20, 2, 2);
    poly(c, '#ff6aa0', [x + 8.2, y - 19, x + 14.8, y - 19, x + 11.5, y - 15]);
  } else {
    poly(c, '#2f8fd9', [x - 8, y - 6, x - 2, y - 22, x + 8, y - 26, x + 10, y - 18, x + 2, y - 8]);
    poly(c, '#7fc0f0', [x - 4, y - 10, x, y - 20, x + 6, y - 22]);
    poly(c, '#2f8fd9', [x + 8, y - 26, x + 14, y - 32, x + 15, y - 24]);
    ellipse(c, '#1b1d24', x - 3, y - 17, 1, 1);
  }
}

/** Info board about the Dome of Light, with a little rainbow picture. */
function domeSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 8, 2);
  for (const px of [x - 7, x + 5]) block(c, px, y - 16, 2.2, 16, '#5a4a3a', '#7a6a58');
  block(c, x - 12, y - 30, 24, 15, '#1f3a6e', '#2f5a9e', '#102040');
  c.fillStyle = '#ff6a3a';
  c.beginPath(); c.arc(x, y - 18, 8, Math.PI, 0); c.fill();
  c.fillStyle = '#ffd34e';
  c.beginPath(); c.arc(x, y - 18, 6, Math.PI, 0); c.fill();
  c.fillStyle = '#3a8fd9';
  c.beginPath(); c.arc(x, y - 18, 4, Math.PI, 0); c.fill();
  c.fillStyle = '#57c278';
  c.beginPath(); c.arc(x, y - 18, 2, Math.PI, 0); c.fill();
  rect(c, '#1f3a6e', x - 12, y - 18, 24, 3);
  txt(c, '光之穹頂', x, y - 27.2, 3.6, '#fff6dc', 900);
}

// ---------------------------------------------------------------------------
// Formosa Boulevard glass shells, Liuhe gate and stalls
// ---------------------------------------------------------------------------

/** Formosa Boulevard entrance: two curved glass shells like praying hands, the Dome's colours glowing inside. */
function glassEntrance(c: Ctx, d: Decor): void {
  const w = d.w ?? 76, h = d.h ?? 76, v = d.variant ?? 0;
  art(c, `khGlass${v}`, d.x, d.y, w, h, (c, night) => {
    shadow(c, 0, 0, w / 2 - 4, 4, 0.3);
    // Base: dark granite, stairs going down into the station.
    block(c, -w / 2 + 6, -10, w - 12, 10, '#5a6068', '#7a8088', '#3a4048');
    rect(c, night ? '#ffd27a' : '#2a3038', -14, -8, 28, 8);
    for (let k = 0; k < 4; k++) rect(c, `rgba(255,255,255,${0.12 + k * 0.06})`, -14, -8 + k * 2, 28, 0.8);
    // The two shells: tall curved glass planes leaning together.
    const shell = (s: number) => {
      c.fillStyle = night ? 'rgba(120,180,230,0.55)' : 'rgba(150,205,235,0.78)';
      c.beginPath();
      c.moveTo(s * 32, -8);
      c.quadraticCurveTo(s * 36, -44, s * 4, -h + 6);
      c.lineTo(s * 1, -h + 10);
      c.quadraticCurveTo(s * 22, -40, s * 20, -8);
      c.closePath();
      c.fill();
      // Ribs.
      c.strokeStyle = 'rgba(255,255,255,0.75)';
      c.lineWidth = 0.9;
      for (let k = 0; k <= 5; k++) {
        const u = k / 5;
        c.beginPath();
        c.moveTo(s * (32 - 12 * u), -8);
        c.quadraticCurveTo(s * (36 - 14 * u), -44, s * (4 - 3 * u), -h + 6 + 4 * u);
        c.stroke();
      }
      for (let y = -16; y > -h + 14; y -= 9) {
        const k = (y + 8) / (-h + 14);
        c.beginPath();
        c.moveTo(s * (32 - 28 * k * k), y);
        c.lineTo(s * (20 - 19 * k * k), y);
        c.stroke();
      }
    };
    if (night) {
      // The dome's colours spilling up through the glass.
      const cols = ['rgba(255,80,120,0.35)', 'rgba(80,160,255,0.35)', 'rgba(255,220,80,0.3)', 'rgba(80,230,160,0.3)'];
      cols.forEach((col, i) => ellipse(c, col, -12 + i * 8, -22 - (i % 2) * 8, 14, 18));
    }
    shell(-1);
    shell(1);
    // Steel frame at the top and the station name.
    rect(c, '#e8ecef', -5, -h + 4, 10, 2.4);
    block(c, -22, -h + 18, 44, 9, '#f07a1a', '#ffa050', '#b05010');
    txt(c, '美麗島站', 0, -h + 22.6, 5.6, '#ffffff', 900, 'rgba(0,0,0,0.3)');
    txt(c, 'Formosa Boulevard', 0, -h + 31, 2.6, night ? '#ffe9b8' : '#1f3a6e', 700);
    // KMRT roundel.
    ellipse(c, '#c8402e', -30, -h + 22, 4.4, 4.4);
    ellipse(c, '#ffffff', -30, -h + 22, 3.2, 3.2);
    ellipse(c, '#c8402e', -30, -h + 22, 1.6, 1.6);
  });
}

/** Liuhe Night Market gate: red pillars, a lit board with 六合夜市, bulbs along the arch. */
function liuheGate(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 72, h = d.h ?? 80, x = d.x, y = d.y, night = isNight();
  art(c, 'khLiuhe', x, y, w, h, (c, night) => {
    shadow(c, 0, 0, w / 2, 3, 0.3);
    for (const px of [-w / 2 + 4, w / 2 - 10]) {
      block(c, px - 2, -6, 10, 6, '#9a9690', '#b8b4ac', '#6a6660');
      block(c, px, -h + 20, 6, h - 26, '#c8402e', '#e05a48', '#8a2a1e');
      for (let yy = -h + 26; yy < -8; yy += 12) rect(c, '#f2c230', px + 1, yy, 4, 1.2);
    }
    block(c, -w / 2 + 2, -h + 6, w - 4, 20, night ? '#2a1a40' : '#f6f0e0', undefined, '#8a2a1e');
    rect(c, '#c8402e', -w / 2 + 2, -h + 6, w - 4, 2.4);
    rect(c, '#c8402e', -w / 2 + 2, -h + 23.6, w - 4, 2.4);
    txt(c, '六合夜市', 0, -h + 14.6, 9, night ? '#ffe070' : '#c8402e', 900, 'rgba(0,0,0,0.3)');
    txt(c, 'LIUHE NIGHT MARKET', 0, -h + 22, 2.6, night ? '#ffffff' : '#1b1d24', 700);
    templeRoof(c, -w / 2 - 2, w / 2 + 2, -h + 5, 7, '#e88a2a', '#2f6a4a');
  });
  // Bulbs along the beam twinkle live at night.
  if (night) {
    for (let i = 0; i < 9; i++) {
      const bx = x - w / 2 + 6 + i * ((w - 12) / 8);
      const on = Math.sin(t * 4 + i * 1.3) > -0.3;
      ellipse(c, on ? '#ffe070' : '#8a6a30', bx, y - h + 27, 1.3, 1.3);
      if (on) ellipse(c, 'rgba(255,220,120,0.25)', bx, y - h + 27, 4, 4);
    }
    ellipse(c, 'rgba(255,120,100,0.18)', x, y - h + 14, w / 2, 12);
  }
}

const STALLS: [string, string][] = [['海產粥', '#2f6fb0'], ['烤魷魚', '#c8402e'], ['牛排', '#8a4a2a'], ['鹽酥雞', '#f07a1a'], ['青草茶', '#2fa36a'], ['木瓜牛奶', '#f2a030']];

/** Night-market stall with the goods of `variant`: seafood, squid, steak, popcorn chicken, drinks, papaya milk. */
function marketStall(c: Ctx, r: Rect, t: number, v: number): void {
  const [label, canopy] = STALLS[v % STALLS.length];
  stall(c, r, t, label, canopy, (c, r, t) => {
    const x = r.x, y = r.y;
    switch (v % STALLS.length) {
      case 0:
        // Fish and crabs on ice, a steaming pot.
        block(c, x + 2, y - 15, r.w - 4, 6, '#d8ecf4', '#f0f8fc', '#8ab0c0');
        for (let k = 0; k < 4; k++) ellipse(c, k % 2 ? '#8ab0d0' : '#e07a5a', x + 7 + k * 9, y - 12, 3.4, 1.6);
        ellipse(c, '#e8703a', x + r.w - 10, y - 13, 3, 2);
        block(c, x + r.w - 14, y - 22, 10, 7, '#9aa0a8', '#c8ccd0', '#5a6068');
        steam(c, x + r.w - 9, y - 22, t, 3, 0.4);
        break;
      case 1:
        // Squid skewers over a glowing grill.
        block(c, x + 4, y - 16, r.w - 8, 6, '#3a3a40', '#5a5a60', '#1b1d24');
        for (let k = 0; k < 5; k++) rect(c, Math.sin(t * 6 + k) > 0 ? '#ff7a3a' : '#e05a2a', x + 7 + k * 7, y - 14, 3, 2);
        for (let k = 0; k < 3; k++) {
          rect(c, '#8a6a4a', x + 8 + k * 12, y - 30, 0.8, 16);
          ellipse(c, '#f0d8c0', x + 8.4 + k * 12, y - 27, 2.4, 4.4);
          for (let j = 0; j < 4; j++) line(c, '#f0d8c0', 0.8, [x + 7 + k * 12 + j * 0.9, y - 23, x + 6.5 + k * 12 + j * 1.2, y - 19]);
        }
        steam(c, x + r.w / 2, y - 20, t, 3, 0.3);
        break;
      case 2:
        // Sizzling steaks on iron plates.
        for (let k = 0; k < 3; k++) {
          ellipse(c, '#2a2a30', x + 9 + k * 13, y - 13, 5.4, 2.6);
          ellipse(c, '#8a4a2a', x + 9 + k * 13, y - 13.6, 3.6, 1.8);
          rect(c, '#f2c230', x + 6 + k * 13, y - 15, 2, 1);
        }
        steam(c, x + r.w / 2, y - 16, t, 4, 0.4);
        break;
      case 3:
        // Baskets of popcorn chicken and basil, a fryer.
        block(c, x + r.w - 16, y - 20, 12, 10, '#9aa0a8', '#c8ccd0', '#5a6068');
        rect(c, '#e8a030', x + r.w - 14, y - 18, 8, 3);
        for (let k = 0; k < 2; k++) {
          ellipse(c, '#c8a060', x + 8 + k * 12, y - 12, 5, 2.6);
          for (let j = 0; j < 5; j++) ellipse(c, '#e8a040', x + 5 + k * 12 + j * 1.6, y - 13.4 - (j % 2), 1.2, 1.1);
          ellipse(c, '#3f8a3a', x + 9 + k * 12, y - 14.6, 1.2, 0.8);
        }
        steam(c, x + r.w - 10, y - 20, t, 2, 0.3);
        break;
      case 4:
        // Herbal tea jugs and cups.
        for (let k = 0; k < 3; k++) block(c, x + 4 + k * 12, y - 22, 9, 12, ['#4a3a20', '#8a6a2a', '#2f6a4a'][k], undefined, '#1b1d24');
        for (let k = 0; k < 4; k++) block(c, x + 3 + k * 10, y - 9, 4, 3, '#f4f1ea', undefined, '#8a8070');
        break;
      default:
        // Papayas, milk and a blender.
        for (let k = 0; k < 3; k++) ellipse(c, k === 1 ? '#f2a030' : '#8ab040', x + 7 + k * 8, y - 13, 3.6, 2.6);
        ellipse(c, '#f0b040', x + 15, y - 13, 2.4, 1.6);
        block(c, x + r.w - 14, y - 24, 8, 14, '#d8e8f0', '#f0f8ff', '#8aa0b0');
        rect(c, '#f2a030', x + r.w - 13, y - 20, 6, 8);
        block(c, x + r.w - 14, y - 12, 8, 2, '#3a3a40');
        for (let k = 0; k < 2; k++) block(c, x + 30 + k * 6, y - 11, 4, 4, '#ffd890', undefined, '#8a6a30');
    }
  });
}

/** Plastic table with two diners and bowls (`variant` seeds the looks). */
function tables(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  person(c, x - 9, y - 6, 4 + v * 7, 'right', 0, t, v);
  person(c, x + 9, y - 6, 9 + v * 5, 'left', 0, t, v + 3);
  shadow(c, x, y, 9, 2.4, 0.3);
  block(c, x - 8, y - 10, 16, 5, '#c84a36', '#e86a50', '#8a2a1e');
  ellipse(c, '#f4f1ea', x - 3, y - 10, 2.4, 1.2);
  ellipse(c, '#f4f1ea', x + 3, y - 10, 2.4, 1.2);
  rect(c, '#9aa0a6', x - 0.7, y - 5, 1.4, 5);
}

/** A row of red plastic stools. */
function stools(c: Ctx, d: Decor): void {
  const w = d.w ?? 40, v = d.variant ?? 0;
  for (let k = 0; k < 4; k++) {
    const x = d.x - w / 2 + 5 + k * (w / 4), y = d.y + (hash(k, v, 71) - 0.5) * 3;
    const col = (k + v) % 3 === 0 ? '#2f6fb0' : '#c84a36';
    shadow(c, x, y, 3.4, 1.2, 0.25);
    rect(c, shade(col, -0.3), x - 2.4, y - 4, 1.2, 4);
    rect(c, shade(col, -0.3), x + 1.2, y - 4, 1.2, 4);
    block(c, x - 3.4, y - 6, 6.8, 2.6, col, shade(col, 0.3), shade(col, -0.3));
  }
}

// ---------------------------------------------------------------------------
// Love River: boats, docks, heart lamps, reflections, cyclists, joggers
// ---------------------------------------------------------------------------

const RIVER_X = 23 * T;
const RIVER_TOP = 28 * T, RIVER_BOT = 43 * T;
/** The promenades run from the boulevard bridge down to the LRT bridge. */
const PROM_TOP = 20 * T, PROM_BOT = 47 * T;

/**
 * Moving props cover long paths, so the map places several segments (`h` tall, bottom at the anchor)
 * and each one draws the traveller only while it is inside — the renderer culls by anchor.
 */
const inSegment = (d: Decor, y: number, h: number) => y > d.y - h && y <= d.y;

/** Love Boat (愛之船): a white solar boat with a canopy and heart badge, cruising between the bridges. */
function loveBoat(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const period = 48;
  const u = ((t / period + v * 0.5) % 1);
  const k = u < 0.5 ? u * 2 : 2 - u * 2;
  const y = RIVER_TOP + (RIVER_BOT - RIVER_TOP) * (0.5 - 0.5 * Math.cos(k * Math.PI));
  if (!inSegment(d, y, d.h ?? 128)) return;
  const x = RIVER_X + (v ? 8 : -8), south = u < 0.5;
  const bob = Math.sin(t * 1.6 + v) * 0.8;
  ellipse(c, 'rgba(10,40,70,0.3)', x, y + 2, 14, 4);
  // Wake.
  for (let i = 1; i <= 3; i++) ellipse(c, `rgba(255,255,255,${0.25 - i * 0.06})`, x, y + (south ? -i * 7 : i * 7), 5 + i * 2, 1.4);
  // Hull (seen from above, long axis north-south).
  c.fillStyle = '#f4f6f8';
  c.beginPath();
  c.moveTo(x - 7, y - 10 + bob);
  c.quadraticCurveTo(x, y - 20 + bob, x + 7, y - 10 + bob);
  c.lineTo(x + 7, y + 12 + bob);
  c.quadraticCurveTo(x, y + 16 + bob, x - 7, y + 12 + bob);
  c.closePath();
  c.fill();
  rect(c, '#2f6fb0', x - 7, y - 2 + bob, 14, 1.2);
  // Passengers on the open deck.
  for (let i = 0; i < 4; i++) ellipse(c, ['#e2493b', '#ffd34e', '#3a8fd9', '#57c278'][i], x - 3.5 + (i % 2) * 7, y - 6 + Math.floor(i / 2) * 7 + bob, 1.6, 1.6);
  // Canopy.
  block(c, x - 6, y + 2 + bob, 12, 8, '#e05a8a', '#ff8ab0', '#a03a5a');
  ellipse(c, '#ffffff', x - 1.5, y + 5.5 + bob, 1.2, 1.2); ellipse(c, '#ffffff', x + 1.5, y + 5.5 + bob, 1.2, 1.2);
  poly(c, '#ffffff', [x - 2.6, y + 6 + bob, x + 2.6, y + 6 + bob, x, y + 8.6 + bob]);
  if (isNight()) ellipse(c, 'rgba(255,150,200,0.3)', x, y + bob, 12, 14);
}

/** Swan pedal boat bobbing near the bank. */
function pedalBoat(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y + Math.sin(t * 1.2 + (d.variant ?? 0)) * 0.8, v = d.variant ?? 0;
  ellipse(c, 'rgba(10,40,70,0.3)', x, y + 1, 10, 3);
  ellipse(c, v ? '#ffd34e' : '#ffffff', x, y - 2, 9, 4);
  rect(c, v ? '#e0a020' : '#dcdcdc', x - 8, y - 2, 16, 1);
  line(c, v ? '#ffd34e' : '#ffffff', 3, [x + 6, y - 4, x + 8, y - 12]);
  ellipse(c, v ? '#ffd34e' : '#ffffff', x + 8.5, y - 13, 2.6, 2.2);
  poly(c, '#f07a1a', [x + 10.5, y - 13.5, x + 14, y - 12.5, x + 10.5, y - 11.5]);
  ellipse(c, '#1b1d24', x + 9, y - 13.8, 0.5, 0.5);
  ellipse(c, '#3a8fd9', x - 3, y - 4, 1.6, 1.6);
  ellipse(c, '#e2493b', x + 1, y - 4, 1.6, 1.6);
}

/** Wooden landing stage with a ticket hut for the Love Boat. */
function dock(c: Ctx, d: Decor): void {
  const w = d.w ?? 32, x = d.x, y = d.y;
  // Planks over the water, west of the anchor.
  block(c, x - w, y - 9, w, 9, '#a8784a', '#c8986a', '#6a4a2a');
  for (let xx = x - w + 3; xx < x; xx += 4) rect(c, 'rgba(0,0,0,0.18)', xx, y - 9, 0.6, 9);
  for (const px of [x - w + 2, x - 3]) rect(c, '#6a4a2a', px, y - 2, 2, 5);
  for (let xx = x - w + 4; xx < x; xx += 8) {
    rect(c, '#dfe3e6', xx, y - 15, 1, 6);
  }
  rect(c, '#dfe3e6', x - w + 4, y - 15, w - 6, 1);
  // Hut with a heart sign.
  block(c, x - 2, y - 24, 12, 24, '#f4f6f8', '#ffffff', '#9aa0a6');
  rect(c, isNight() ? '#ffe4a8' : '#cfe4ee', x, y - 20, 8, 8);
  block(c, x - 3, y - 28, 14, 5, '#e05a8a', '#ff8ab0', '#a03a5a');
  txt(c, '愛之船', x + 4, y - 25.4, 3, '#ffffff', 900);
}

/** Heart lamp post on the promenade (the hearts glow pink at night); `w` < 0 mirrors it. */
function heartRail(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, night = isNight(), s = (d.w ?? 1) < 0 ? -1 : 1;
  shadow(c, x, y, 3, 1.2);
  rect(c, '#3a4a5a', x - 1, y - 30, 2, 30);
  line(c, '#3a4a5a', 1.4, [x, y - 30, x + s * 6, y - 34]);
  const hx = x + s * 7, hy = y - 36;
  const col = night ? '#ff6aa0' : '#e05a8a';
  ellipse(c, col, hx - 1.8, hy - 1, 2.1, 2.1); ellipse(c, col, hx + 1.8, hy - 1, 2.1, 2.1);
  poly(c, col, [hx - 3.8, hy - 0.2, hx + 3.8, hy - 0.2, hx, hy + 4]);
  if (night) {
    ellipse(c, 'rgba(255,120,170,0.35)', hx, hy, 7, 7);
    ellipse(c, 'rgba(255,230,200,0.8)', x, y - 31, 2, 1.2);
  }
}

/** Riverside café kiosk with a parasol. */
function cafe(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  art(c, 'khCafe', x, y, d.w ?? 40, 44, (c, night) => {
    shadow(c, 0, 0, 18, 3, 0.3);
    block(c, -16, -22, 32, 22, '#f6efe2', '#ffffff', '#a89888');
    rect(c, night ? '#ffe4a8' : '#cfe4ee', -13, -18, 12, 10);
    block(c, 2, -14, 11, 14, '#8a5530', '#a8704a', '#5a3520');
    block(c, -17, -28, 34, 6, '#2f6a4a', '#4a8a6a', '#1f4a30');
    txt(c, 'River Café 河岸咖啡', 0, -24.8, 3, '#ffffff', 800);
    rect(c, '#9aa0a6', 14, -36, 1, 14);
    poly(c, '#e2493b', [4, -34, 24, -34, 14, -42]);
    poly(c, '#f4f1ea', [8, -34, 20, -34, 14, -39]);
  });
}

/** Coloured light reflections sliding on the river at night (Love River's famous light show). */
function riverGlow(c: Ctx, d: Decor, t: number): void {
  if (!isNight()) return;
  const w = d.w ?? 64, h = d.h ?? 128, L = d.x - w / 2, top = d.y - h;
  const cols = ['255,100,170', '140,110,255', '70,170,255', '255,190,110'];
  // A dusky wash so the river reads darker than by day, then the sliding colour streaks.
  for (let yy = top; yy < d.y; yy += T) if (yy < 34 * T || yy >= 37 * T) rect(c, 'rgba(20,20,60,0.28)', L + 2, yy, w - 4, T);
  for (let i = 0; i < 12; i++) {
    const yy = top + ((h1(i, 5 + d.y) * h + t * 5 * (0.5 + h1(i, 6))) % h);
    if (yy >= 34 * T - 2 && yy < 37 * T + 2) continue; // the road bridge
    const xx = L + 6 + h1(i, 7) * (w - 12) + Math.sin(t * 0.8 + i) * 3;
    const a = 0.3 + 0.18 * Math.sin(t * 1.4 + i * 0.9);
    ellipse(c, `rgba(${cols[i % 4]},${a})`, xx, yy, 7 + h1(i, 8) * 9, 1.8);
    ellipse(c, `rgba(255,255,255,${a * 0.35})`, xx - 2, yy - 0.4, 2.5, 0.6);
  }
}

/** A cyclist riding up and down the promenade segment (`h` tall, bottom at the anchor). */
function cyclist(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const period = 18 + v * 4;
  const u = ((t / period + v * 0.37) % 1);
  const south = u < 0.5;
  const k = south ? u * 2 : 2 - u * 2;
  const x = d.x, y = PROM_TOP + (PROM_BOT - PROM_TOP) * k;
  if (!inSegment(d, y, d.h ?? 112)) return;
  const look = lookOf(12 + v * 9);
  // Bike seen from behind/front: two wheels in line, the rider on top.
  ellipse(c, 'rgba(20,10,6,0.25)', x, y + 1, 5, 1.6);
  rect(c, '#1b1d24', x - 1, y - 6, 2, 7);
  ellipse(c, '#2a2e33', x, y - 6, 1.4, 2.4);
  rect(c, '#3a8fd9', x - 1.2, y - 12, 2.4, 6);
  rect(c, '#1b1d24', x - 4, y - 13, 8, 1);
  stand(c, look, x, y - 4, south ? 'down' : 'up', t, v, true, (t * 2) % 1);
  // Helmet.
  ellipse(c, v ? '#e2493b' : '#ffd34e', x, y - 24, 3.2, 1.8);
}

/** A jogger running along the promenade segment. */
function jogger(c: Ctx, d: Decor, t: number): void {
  const period = 14;
  const u = (t / period) % 1, south = u < 0.5;
  const k = south ? u * 2 : 2 - u * 2;
  const y = PROM_TOP + (PROM_BOT - PROM_TOP) * k;
  if (!inSegment(d, y, d.h ?? 112)) return;
  stand(c, { ...lookOf(5), top: '#ff6aa0', topShade: '#d04a80', bottom: '#1b1d24', hairStyle: 'pony' }, d.x, y, south ? 'down' : 'up', t, 2, true, (t * 2.6) % 1);
}

// ---------------------------------------------------------------------------
// Pier-2: murals, containers, graffiti, artist, LRT, skater
// ---------------------------------------------------------------------------

/** Big mural on a warehouse front: 0 whale and waves, 1 geometric colour blocks, 2 giant cat. */
function mural(c: Ctx, d: Decor): void {
  const w = d.w ?? 96, x = d.x, y = d.y, v = d.variant ?? 0;
  art(c, `khMural${v}:${w}`, x, y, w, 40, (c) => {
    const L = -w / 2, R = w / 2;
    if (v === 0) {
      rect(c, '#1f5fa8', L, -38, w, 34);
      for (let k = 0; k < 4; k++) {
        c.strokeStyle = 'rgba(255,255,255,0.6)';
        c.lineWidth = 1.2;
        c.beginPath();
        for (let xx = L; xx <= R; xx += 4) c.lineTo(xx, -14 - k * 6 + Math.sin(xx / 7 + k) * 2.2);
        c.stroke();
      }
      ellipse(c, '#2a2f3d', 0, -24, 22, 8);
      poly(c, '#2a2f3d', [20, -24, 32, -32, 30, -18]);
      ellipse(c, '#f4f1ea', -4, -20, 16, 4);
      ellipse(c, '#ffffff', -14, -26, 1.4, 1.4);
      for (let k = 0; k < 6; k++) ellipse(c, 'rgba(255,255,255,0.7)', -10 + k * 2.4, -32 - (k % 3) * 2, 0.9, 0.9);
    } else if (v === 1) {
      rect(c, '#f4f1ea', L, -38, w, 34);
      for (let i = 0; i < 12; i++) {
        const col = ART_COLS[i % ART_COLS.length];
        const bx = L + (i % 6) * (w / 6), by = -38 + Math.floor(i / 6) * 17;
        if (i % 3 === 0) poly(c, col, [bx, by + 17, bx + w / 6, by + 17, bx + w / 12, by]);
        else if (i % 3 === 1) ellipse(c, col, bx + w / 12, by + 8.5, w / 14, 7);
        else rect(c, col, bx + 2, by + 2, w / 6 - 4, 13);
      }
    } else {
      rect(c, '#ffd34e', L, -38, w, 34);
      ellipse(c, '#2a2a30', 0, -16, 20, 12);
      ellipse(c, '#2a2a30', 0, -28, 12, 10);
      poly(c, '#2a2a30', [-11, -32, -6, -40, -2, -32]);
      poly(c, '#2a2a30', [2, -32, 6, -40, 11, -32]);
      ellipse(c, '#ffd34e', -4, -28, 2.2, 2.6); ellipse(c, '#ffd34e', 4, -28, 2.2, 2.6);
      ellipse(c, '#1b1d24', -4, -28, 1, 2); ellipse(c, '#1b1d24', 4, -28, 1, 2);
      poly(c, '#ff9fc0', [-1.4, -24, 1.4, -24, 0, -22]);
      line(c, '#2a2a30', 0.8, [-8, -23, -16, -24]); line(c, '#2a2a30', 0.8, [8, -23, 16, -24]);
      txt(c, 'PIER-2', R - 14, -8, 5, '#2a2a30', 900);
    }
  }, false);
}

const CONTAINER_COLS = ['#c8402e', '#2f6fb0', '#2fa36a', '#f07a1a', '#8a4aa8', '#5a6470'];

/** Stacked shipping containers turned into art space (`variant` picks the stack). */
function containers(c: Ctx, d: Decor): void {
  const w = d.w ?? 80, x = d.x, y = d.y, v = d.variant ?? 0;
  art(c, `khCont${v}:${w}`, x, y, w, 48, (c, night) => {
    shadow(c, 0, 0, w / 2, 3, 0.3);
    const box = (bx: number, by: number, bw: number, col: string, door: boolean) => {
      block(c, bx, by - 16, bw, 16, col, shade(col, 0.2), shade(col, -0.35));
      for (let xx = bx + 2; xx < bx + bw - 1; xx += 3) rect(c, 'rgba(0,0,0,0.15)', xx, by - 15, 0.7, 14);
      if (door) {
        rect(c, night ? '#ffe4a8' : '#2a2a30', bx + bw / 2 - 5, by - 13, 10, 13);
        rect(c, shade(col, -0.4), bx + bw / 2 - 0.5, by - 13, 1, 13);
      }
      rect(c, '#f4f1ea', bx + 2, by - 14.5, 6, 1.4);
    };
    if (v === 0) {
      box(-w / 2 + 2, 0, 44, CONTAINER_COLS[0], true);
      box(-w / 2 + 48, 0, 30, CONTAINER_COLS[1], false);
      box(-w / 2 + 12, -16, 40, CONTAINER_COLS[2], true);
      rect(c, '#9aa0a6', -w / 2 + 46, -32, 1, 32);
      rect(c, '#9aa0a6', -w / 2 + 46, -32, 10, 1);
      txt(c, 'ART', -w / 2 + 32, -24, 6, '#ffffff', 900);
    } else {
      box(-w / 2 + 2, 0, w - 4, CONTAINER_COLS[3], true);
      box(-w / 2 + 10, -16, w - 20, CONTAINER_COLS[4], false);
      txt(c, '駁二', 0, -24, 6, '#ffffff', 900);
    }
  });
}

/** Free-standing graffiti wall with tags and a cartoon face. */
function graffitiWall(c: Ctx, d: Decor): void {
  const w = d.w ?? 32, x = d.x, y = d.y;
  art(c, `khGraf:${w}`, x, y, w, 30, (c) => {
    shadow(c, 0, 0, w / 2, 2, 0.3);
    block(c, -w / 2, -24, w, 24, '#c8c0b0', '#e0d8c8', '#8a8070');
    poly(c, '#e2493b', [-w / 2 + 3, -8, -w / 2 + 8, -20, -w / 2 + 14, -10, -w / 2 + 18, -18, -w / 2 + 20, -6]);
    ellipse(c, '#3a8fd9', 6, -14, 6, 6);
    ellipse(c, '#ffffff', 4, -15, 1.4, 1.4); ellipse(c, '#ffffff', 8, -15, 1.4, 1.4);
    line(c, '#1b1d24', 0.8, [3, -11, 9, -11]);
    txt(c, 'KHH', -2, -4.4, 4, '#ffd34e', 900);
  }, false);
}

/** An artist at an easel, brush hand moving. */
function artist(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  // Easel and canvas.
  shadow(c, x + 12, y, 6, 1.6);
  line(c, '#8a6a40', 1.2, [x + 8, y, x + 12, y - 26]);
  line(c, '#8a6a40', 1.2, [x + 16, y, x + 12, y - 26]);
  line(c, '#8a6a40', 1.2, [x + 12, y - 26, x + 12, y - 2]);
  block(c, x + 6, y - 24, 12, 14, '#f8f6f0', '#ffffff', '#a8a098');
  ellipse(c, '#2f8fd9', x + 12, y - 16, 4, 2.6);
  ellipse(c, '#f07a1a', x + 9, y - 20, 1.6, 1.6);
  const prog = (t * 0.5) % 1;
  rect(c, '#e2493b', x + 7, y - 12, 10 * prog, 1);
  stand(c, { ...lookOf(22), top: '#f4f1ea', topShade: '#cfc8bb', apron: '#6a4a8a', hairStyle: 'messy' }, x, y, 'right', t, 4);
  // Palette.
  ellipse(c, '#c8a060', x - 6, y - 10 + Math.sin(t * 3) * 0.6, 3, 2);
  for (let k = 0; k < 4; k++) ellipse(c, ART_COLS[k], x - 7.5 + k * 1.1, y - 10.6 + Math.sin(t * 3) * 0.6 + (k % 2) * 0.8, 0.6, 0.6);
}

const LRT_STOPS: [string, string][] = [['C12 駁二大義', 'Dayi Pier-2'], ['C14 哈瑪星', 'Hamasen']];

/** LRT platform: low kerb, glass shelter, stop name board. */
function lrtStop(c: Ctx, d: Decor): void {
  const w = d.w ?? 72, x = d.x, y = d.y, v = (d.variant ?? 0) % LRT_STOPS.length;
  art(c, `khLrtStop${v}`, x, y, w, 40, (c, night) => {
    shadow(c, 0, 0, w / 2, 3, 0.25);
    block(c, -w / 2, -3, w, 3, '#c8c4bc', '#e0dcd4', '#8a867e');
    rect(c, '#f2c230', -w / 2 + 2, -3.2, w - 4, 0.9);
    for (const px of [-w / 2 + 6, w / 2 - 8]) rect(c, '#5a6470', px, -28, 2, 25);
    rect(c, night ? 'rgba(255,230,180,0.45)' : 'rgba(170,210,235,0.5)', -w / 2 + 8, -26, w - 16, 20);
    block(c, -w / 2 + 4, -32, w - 8, 5, '#e8ecef', '#ffffff', '#8a9096');
    block(c, -w / 2 + 10, -22, 30, 3, '#9a6a40', '#b8885a', '#6a4a2a');
    block(c, w / 2 - 28, -24, 20, 10, '#2fa36a', '#4fc38a', '#1f7a4a');
    txt(c, LRT_STOPS[v][0], w / 2 - 18, -20.4, 3, '#ffffff', 900);
    txt(c, LRT_STOPS[v][1], w / 2 - 18, -16.4, 2.2, '#ffffff', 700);
  });
}

/** The circular light rail (CAF Urbos, green and white) gliding along the grass track, pausing at stops. */
const TRAM_W = 128;
function tram(c: Ctx, d: Decor, t: number): void {
  const w = TRAM_W, y = d.y - 6, seg = d.w ?? 256;
  const x0 = 2 * T + w / 2, x1 = 93 * T - w / 2;
  const period = 60;
  const u = (t / period) % 1;
  const k = u < 0.5 ? u * 2 : 2 - u * 2;
  // Ease so it slows into the ends and dwells there.
  const e = k < 0.08 ? 0 : k > 0.92 ? 1 : (k - 0.08) / 0.84;
  const x = x0 + (x1 - x0) * (0.5 - 0.5 * Math.cos(e * Math.PI));
  if (x < d.x - seg / 2 || x >= d.x + seg / 2) return;
  const night = isNight();
  ellipse(c, 'rgba(10,20,30,0.3)', x, y + 6, w / 2, 3.5);
  // Three articulated sections.
  for (let s = 0; s < 3; s++) {
    const sx = x - w / 2 + s * (w / 3) + 1, sw = w / 3 - 2;
    block(c, sx, y - 18, sw, 24, '#f4f6f4', '#ffffff', '#8a9a90');
    rect(c, '#2fa36a', sx, y - 2, sw, 5);
    rect(c, '#1f7a4a', sx, y - 18, sw, 1.6);
    for (let xx = sx + 3; xx < sx + sw - 6; xx += 9) rect(c, night ? '#ffe4a8' : '#9fcbe3', xx, y - 15, 7, 9);
    if (s === 1) rect(c, '#2a2a30', sx + sw / 2 - 4, y - 16, 8, 20);
  }
  // Rounded cab ends.
  for (const s of [-1, 1]) {
    const ex = x + s * (w / 2);
    poly(c, '#f4f6f4', [ex, y - 16, ex + s * 5, y - 8, ex + s * 5, y + 4, ex, y + 6]);
    rect(c, '#2fa36a', ex - (s < 0 ? 0 : 0), y - 2, s * 5, 5);
    rect(c, night ? '#ffffff' : '#2a3a44', ex + (s < 0 ? -4 : 1), y - 14, 3, 6);
    if (night) ellipse(c, 'rgba(255,255,220,0.5)', ex + s * 8, y - 2, 6, 3);
  }
  txt(c, '高雄輕軌', x, y - 20.6, 2.6, '#2fa36a', 900);
}

/** Low posts with a lamp and a signal along the track (the Kaohsiung LRT has no overhead wires). */
function trackPosts(c: Ctx, d: Decor): void {
  const w = d.w ?? 128, L = d.x - w / 2, y = d.y, night = isNight();
  for (let x = L; x <= L + w; x += 64) {
    rect(c, '#5a6470', x - 0.8, y - 22, 1.6, 22);
    block(c, x - 2.4, y - 26, 4.8, 4, '#2a2e33', '#4a4e54');
    ellipse(c, night ? '#ffe4a8' : '#f4f1ea', x, y - 24, 1.6, 1.2);
    ellipse(c, (Math.floor(x / 64) % 2 ? '#57c278' : '#e2493b'), x + 2.6, y - 18, 1, 1);
  }
}

/** Skateboarder rolling back and forth across `w`. */
function skater(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 40, u = (t / 6) % 1, east = u < 0.5;
  const k = east ? u * 2 : 2 - u * 2;
  const x = d.x - w / 2 + w * k, y = d.y;
  ellipse(c, 'rgba(20,10,6,0.25)', x, y + 1, 6, 1.6);
  block(c, x - 6, y - 3, 12, 1.6, '#8a4aa8', '#b06ad0', '#5a2a78');
  ellipse(c, '#1b1d24', x - 4, y - 1, 1.2, 1.2); ellipse(c, '#1b1d24', x + 4, y - 1, 1.2, 1.2);
  stand(c, { ...lookOf(14), hairStyle: 'cap', hat: '#1b1d24', top: '#ffd34e', topShade: '#d8b030' }, x, y - 3, east ? 'right' : 'left', t, 1, false);
}

// ---------------------------------------------------------------------------
// The harbour: 85 Sky Tower, quay, ferry, boats, gulls, kites, Cijin
// ---------------------------------------------------------------------------

/** The 85 Sky Tower (高雄85大樓): two 39-floor legs and a central shaft to a spire, shaped like 高. */
function tower85(c: Ctx, d: Decor): void {
  const w = d.w ?? 112, H = 270, k = d.variant ?? 0;
  slice(c, 'kh85', d.x, d.y + k * 90, w, H, k, (c, night) => {
    shadow(c, 0, 0, w / 2 - 4, 4, 0.3);
    const col = '#c9d3dc', dark = '#8a98a6';
    // Legs.
    for (const s of [-1, 1]) {
      const lx = s < 0 ? -w / 2 + 6 : w / 2 - 36;
      block(c, lx, -150, 30, 150, col, shade(col, 0.1), dark);
      for (let y = -144; y < -8; y += 8) {
        for (let xx = lx + 3; xx < lx + 27; xx += 6) rect(c, night ? (h1(xx, y) > 0.3 ? '#ffe4a8' : '#2a3440') : '#7fb0d0', xx, y, 4, 5);
      }
    }
    // Bridge between the legs and the central shaft.
    block(c, -w / 2 + 6, -164, w - 12, 16, col, shade(col, 0.12), dark);
    for (let xx = -w / 2 + 10; xx < w / 2 - 10; xx += 6) rect(c, night ? '#ffe4a8' : '#7fb0d0', xx, -160, 4, 7);
    block(c, -16, -244, 32, 82, col, shade(col, 0.12), dark);
    for (let y = -240; y < -168; y += 8) for (let xx = -13; xx < 14; xx += 6) rect(c, night ? (h1(xx, y) > 0.35 ? '#ffe4a8' : '#2a3440') : '#7fb0d0', xx, y, 4, 5);
    // Observation deck, crown and spire.
    block(c, -19, -252, 38, 9, '#e8eef2', '#ffffff', dark);
    rect(c, night ? '#ffd27a' : '#5fa0d0', -17, -250, 34, 4);
    block(c, -10, -258, 20, 6, col, shade(col, 0.12), dark);
    rect(c, '#9aa4ac', -1, -270, 2, 12);
    // Ground floor entrance and the hotel sign.
    rect(c, night ? '#ffe4a8' : '#cfe0ea', -w / 2 + 40, -12, w - 80, 12);
    txt(c, '85 SKY TOWER', 0, -170.6, 4.4, night ? '#ffd27a' : '#1f3a6e', 900);
    if (night) {
      ellipse(c, 'rgba(140,200,255,0.18)', 0, -200, 30, 70);
      ellipse(c, 'rgba(255,60,60,0.8)', 0, -270, 1.6, 1.6);
    }
  });
}

/** Mooring bollard with a coil of rope (variant 1: rope leading off to the water). */
function bollard(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  shadow(c, x, y, 4, 1.4, 0.3);
  block(c, x - 3, y - 7, 6, 7, '#2a2e33', '#4a4e54', '#15171b');
  ellipse(c, '#4a4e54', x, y - 7, 3.4, 1.4);
  ellipse(c, '#1b1d24', x, y - 7.6, 2.2, 0.9);
  if (v) {
    c.strokeStyle = '#c8b48a';
    c.lineWidth = 1.2;
    c.beginPath();
    c.moveTo(x - 2, y - 5);
    c.quadraticCurveTo(x + 6, y - 2, x + 12, y + 8);
    c.stroke();
  } else {
    for (let k = 0; k < 3; k++) ellipse(c, '#c8b48a', x, y - 4.5 + k * 1.3, 4 - k * 0.3, 1);
  }
}

/** Life buoy on a white post. */
function lifebuoy(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  shadow(c, x, y, 3, 1.2);
  rect(c, '#dfe3e6', x - 1, y - 24, 2, 24);
  block(c, x - 5, y - 26, 10, 4, '#2f6fb0', '#4a8ad0', '#1f4a80');
  c.strokeStyle = v ? '#f07a1a' : '#e2493b';
  c.lineWidth = 3;
  c.beginPath(); c.arc(x, y - 15, 5, 0, Math.PI * 2); c.stroke();
  c.strokeStyle = '#ffffff';
  c.lineWidth = 3;
  for (let k = 0; k < 4; k++) {
    c.beginPath(); c.arc(x, y - 15, 5, k * Math.PI / 2 + 0.3, k * Math.PI / 2 + 0.75); c.stroke();
  }
}

/** Angler on the quay: rod out over the water, bucket beside, the tip twitching now and then. */
function fisherman(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const look: CharLook = v ? { ...lookOf(19), hairStyle: 'cap', hat: '#2f4a8a' } : { ...lookOf(27), hairStyle: 'cap', hat: '#f4f1ea' };
  stand(c, look, x, y, 'down', t, v);
  const tw = Math.sin(t * 5 + v) > 0.92 ? 2 : 0;
  line(c, '#3a3e44', 0.9, [x + 5, y - 12, x + 16, y - 26 + tw]);
  line(c, 'rgba(255,255,255,0.6)', 0.4, [x + 16, y - 26 + tw, x + 19, y + 14]);
  block(c, x - 10, y - 5, 6, 5, '#2f6fb0', '#4a8ad0', '#1f4a80');
  ellipse(c, '#7fb0d0', x - 7, y - 5, 2.4, 0.8);
}

/** Surfboards leaning on a rack by the ferry. */
function surfboards(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 9, 2, 0.3);
  rect(c, '#8a6a40', x - 9, y - 10, 18, 1.4);
  for (const px of [x - 8, x + 7]) rect(c, '#8a6a40', px, y - 10, 1.4, 10);
  const cols = ['#ffd34e', '#3a8fd9', '#ff6aa0', '#57c278'];
  cols.forEach((col, i) => {
    const bx = x - 6 + i * 4;
    c.fillStyle = col;
    c.beginPath();
    c.ellipse(bx, y - 12, 2, 12, 0.12 * (i - 1.5), 0, Math.PI * 2);
    c.fill();
    line(c, 'rgba(255,255,255,0.6)', 0.6, [bx, y - 21, bx + 0.6, y - 5]);
  });
}

/** "鼓山渡輪站 Gushan Ferry" sign on a post with a little ferry pictogram. */
function ferrySign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 3, 1.2);
  rect(c, '#5a6470', x - 0.9, y - 28, 1.8, 28);
  block(c, x - 13, y - 40, 26, 13, '#1f4fa8', '#3a6fc8', '#102a60');
  txt(c, '鼓山渡輪站', x, y - 35.4, 3.6, '#ffffff', 900);
  txt(c, 'FERRY → 旗津 Cijin', x, y - 30.6, 2.2, '#ffd34e', 800);
}

/** Gulls circling over the water, flapping. */
function gulls(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 200, h = d.h ?? 60, v = d.variant ?? 0;
  for (let i = 0; i < 5; i++) {
    const ph = t * (0.12 + h1(i, v) * 0.08) + i * 1.3;
    const x = d.x - w / 2 + ((h1(i, v + 1) * w + ph * 40) % w);
    const y = d.y - h + h1(i, v + 2) * h + Math.sin(ph * 2) * 4;
    const flap = Math.sin(t * 7 + i) * 2.2;
    line(c, '#f4f1ea', 1.1, [x - 5, y - flap, x, y + 1, x + 5, y - flap]);
    line(c, '#3a3e44', 0.5, [x - 5, y - flap + 0.6, x, y + 1.6, x + 5, y - flap + 0.6]);
  }
}

/** Kites high over the harbour park: diamonds and a long dragon kite, drifting. */
function kites(c: Ctx, d: Decor, t: number): void {
  const tm = now();
  if (tm < 8 * 60 || tm >= 19 * 60) return;
  const w = d.w ?? 160, h = d.h ?? 120;
  const sw = wind.at(d.x, d.y) * 6;
  const kite = (x: number, y: number, col: string, s: number) => {
    poly(c, col, [x, y - 8 * s, x + 5 * s, y, x, y + 8 * s, x - 5 * s, y]);
    line(c, 'rgba(0,0,0,0.3)', 0.5, [x, y - 8 * s, x, y + 8 * s]);
    line(c, col, 0.8, [x, y + 8 * s, x - 2 * s + sw * 0.2, y + 16 * s, x + 2 * s, y + 22 * s]);
  };
  for (let i = 0; i < 3; i++) {
    const x = d.x - w / 2 + h1(i, 3) * w + Math.sin(t * 0.6 + i) * 6 + sw;
    const y = d.y - h + h1(i, 4) * h * 0.7 + Math.cos(t * 0.8 + i) * 4;
    kite(x, y, ART_COLS[i * 2], 0.9 + i * 0.2);
  }
  // Dragon kite: a head and a long wavy tail of discs.
  const hx = d.x + w / 4 + sw, hy = d.y - h * 0.8 + Math.sin(t * 0.7) * 3;
  for (let k = 12; k >= 0; k--) ellipse(c, k % 2 ? '#e2493b' : '#ffd34e', hx + k * 4 + Math.sin(t * 2 + k * 0.6) * 2, hy + k * 1.6 + Math.sin(t * 3 + k * 0.8) * 2.4, 2.2 - k * 0.08, 2);
  ellipse(c, '#2f8a5a', hx, hy, 3.6, 3);
  ellipse(c, '#ffffff', hx - 1, hy - 0.8, 0.9, 0.9); ellipse(c, '#ffffff', hx + 1, hy - 0.8, 0.9, 0.9);
}

/** Family picnic: a mat, two people and a basket. */
function picnic(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 40;
  rect(c, '#2f6fb0', x - w / 2, y - 10, w, 12);
  for (let k = 0; k < w; k += 6) rect(c, 'rgba(255,255,255,0.35)', x - w / 2 + k, y - 10, 2.5, 12);
  person(c, x - 8, y - 6, 20, 'right', 0, t, 1);
  person(c, x + 8, y - 6, 4, 'left', 5, t, 2);
  rect(c, '#2f6fb0', x - w / 2, y - 4, w, 6);
  for (let k = 0; k < w; k += 6) rect(c, 'rgba(255,255,255,0.35)', x - w / 2 + k, y - 4, 2.5, 6);
  block(c, x - 4, y - 7, 8, 5, '#c8a060', '#e0c080', '#8a6a30');
  ellipse(c, '#e2493b', x - 1, y - 8, 1.4, 1.4);
}

/** Waves lapping the quay wall (a wide, flat, animated layer just below the quay edge). */
function surf(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 256, L = d.x - w / 2, y = d.y;
  for (let x = L; x < L + w; x += 24) {
    const ph = (t * 0.5 + x * 0.013) % 1;
    const a = 0.45 * (1 - ph);
    ellipse(c, `rgba(255,255,255,${a})`, x + 12 + Math.sin(t + x) * 3, y + 1 + ph * 5, 10 + ph * 6, 1.1);
  }
  rect(c, 'rgba(255,255,255,0.35)', L, y + 0.4, w, 0.7);
}

/** Ferry pier: wooden deck posts, railings and a small canopy at the end. */
function pierKh(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, h = d.h ?? 48, L = d.x - w / 2, top = d.y - h;
  // Posts in the water on both sides.
  for (let y = top + 8; y <= d.y; y += 12) {
    for (const px of [L - 2, L + w]) {
      rect(c, '#5a4a3a', px, y - 10, 2.6, 12);
      ellipse(c, 'rgba(255,255,255,0.3)', px + 1.3, y + 2, 3, 1);
    }
  }
  // Railings.
  for (const px of [L + 1, L + w - 2.4]) {
    rect(c, '#dfe3e6', px, top - 2, 1.4, h);
    for (let y = top; y < d.y; y += 8) rect(c, '#dfe3e6', px - 0.6, y, 2.6, 1.2);
  }
  rect(c, '#dfe3e6', L + 1, top - 9, w - 2, 1.4);
  for (let x = L + 1; x < L + w; x += 8) rect(c, '#dfe3e6', x, top - 9, 1.2, 9);
}

/** The orange-and-white Cijin ferry moored beside the pier, bobbing, scooters on the car deck. */
function ferry(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 96, x = d.x, y = d.y + Math.sin(t * 1.1) * 1.2, night = isNight();
  ellipse(c, 'rgba(10,40,70,0.35)', x, y + 3, w / 2, 5);
  // Hull.
  c.fillStyle = '#f4f6f8';
  c.beginPath();
  c.moveTo(x - w / 2 + 6, y - 10);
  c.lineTo(x + w / 2 - 2, y - 10);
  c.quadraticCurveTo(x + w / 2 + 6, y - 4, x + w / 2 - 4, y + 3);
  c.lineTo(x - w / 2 + 2, y + 3);
  c.quadraticCurveTo(x - w / 2 - 4, y - 4, x - w / 2 + 6, y - 10);
  c.fill();
  rect(c, '#f07a1a', x - w / 2 + 4, y - 4, w - 6, 3);
  rect(c, '#1f4fa8', x - w / 2 + 4, y - 1, w - 6, 1);
  // Upper deck cabin with windows and the wheelhouse.
  block(c, x - w / 2 + 12, y - 22, w - 24, 12, '#f4f6f8', '#ffffff', '#9aa0a6');
  for (let xx = x - w / 2 + 16; xx < x + w / 2 - 16; xx += 8) rect(c, night ? '#ffe4a8' : '#7fb0d0', xx, y - 20, 5, 5);
  rect(c, '#f07a1a', x - w / 2 + 12, y - 12, w - 24, 2);
  block(c, x + w / 2 - 34, y - 30, 16, 8, '#f4f6f8', '#ffffff', '#9aa0a6');
  rect(c, night ? '#ffe4a8' : '#2a3a44', x + w / 2 - 32, y - 28, 12, 4);
  rect(c, '#1b1d24', x + w / 2 - 22, y - 36, 1.6, 6);
  poly(c, '#e2493b', [x + w / 2 - 20.4, y - 36, x + w / 2 - 14, y - 34, x + w / 2 - 20.4, y - 32]);
  // Scooters parked on the car deck.
  for (let k = 0; k < 4; k++) {
    const sx = x - w / 2 + 18 + k * 10;
    ellipse(c, '#1b1d24', sx - 2, y - 7, 1.4, 1.4); ellipse(c, '#1b1d24', sx + 2, y - 7, 1.4, 1.4);
    block(c, sx - 2.6, y - 11, 5.2, 3.4, ['#e2493b', '#3a8fd9', '#f4f1ea', '#57c278'][k], undefined, '#1b1d24');
  }
  txt(c, '旗津', x + 10, y - 7.4, 4, '#1f4fa8', 900);
  if (night) ellipse(c, 'rgba(255,200,120,0.25)', x, y - 16, w / 2, 10);
}

/** Small fishing boat at anchor, bobbing, with nets and buoys. */
function fishBoat(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 60, v = d.variant ?? 0, x = d.x, y = d.y + Math.sin(t * 1.3 + v) * 1;
  const hull = ['#2f6fb0', '#e2493b', '#f4f1ea'][v % 3], stripe = ['#ffffff', '#ffffff', '#2f6fb0'][v % 3];
  ellipse(c, 'rgba(10,40,70,0.3)', x, y + 2, w / 2, 3.5);
  c.fillStyle = hull;
  c.beginPath();
  c.moveTo(x - w / 2 + 2, y - 7);
  c.lineTo(x + w / 2 - 6, y - 8);
  c.quadraticCurveTo(x + w / 2 + 2, y - 3, x + w / 2 - 8, y + 2);
  c.lineTo(x - w / 2 + 6, y + 2);
  c.quadraticCurveTo(x - w / 2 - 2, y - 2, x - w / 2 + 2, y - 7);
  c.fill();
  rect(c, stripe, x - w / 2 + 3, y - 4, w - 10, 1.2);
  block(c, x - 6, y - 16, 14, 9, '#f4f6f8', '#ffffff', '#9aa0a6');
  rect(c, '#2a3a44', x - 4, y - 14, 4, 3); rect(c, '#2a3a44', x + 2, y - 14, 4, 3);
  rect(c, '#8a6a40', x - 14, y - 24, 1.4, 17);
  line(c, '#3a3e44', 0.5, [x - 13, y - 23, x + 8, y - 16]);
  for (let k = 0; k < 3; k++) ellipse(c, k % 2 ? '#f07a1a' : '#ffd34e', x + 12 + k * 4, y - 9, 1.6, 1.6);
  ellipse(c, 'rgba(120,180,120,0.6)', x + 14, y - 12, 6, 3);
  for (let k = -5; k <= 5; k += 2) line(c, 'rgba(60,90,60,0.5)', 0.4, [x + 14 + k, y - 15, x + 14 + k * 0.6, y - 9]);
  if (isNight()) ellipse(c, 'rgba(255,220,160,0.6)', x - 13, y - 25, 1.4, 1.4);
}

/** A container ship far out in the harbour, stacked boxes and a tall bridge. */
function cargoShip(c: Ctx, d: Decor): void {
  const w = d.w ?? 180, x = d.x, y = d.y;
  art(c, 'khCargo', x, y, w, 40, (c, night) => {
    c.fillStyle = '#2a3a4a';
    c.beginPath();
    c.moveTo(-w / 2 + 4, -10);
    c.lineTo(w / 2 - 2, -10);
    c.lineTo(w / 2 - 10, 0);
    c.lineTo(-w / 2 + 2, 0);
    c.closePath();
    c.fill();
    rect(c, '#c8402e', -w / 2 + 6, -3, w - 14, 2);
    for (let xx = -w / 2 + 12; xx < w / 2 - 40; xx += 9) {
      for (let s = 0; s < 3; s++) rect(c, CONTAINER_COLS[(Math.round(xx) + s) % CONTAINER_COLS.length], xx, -16 - s * 5, 8, 5);
    }
    block(c, w / 2 - 36, -30, 22, 20, '#e8ecef', '#ffffff', '#8a9096');
    for (let s = 0; s < 3; s++) for (let xx = w / 2 - 33; xx < w / 2 - 17; xx += 5) rect(c, night ? '#ffe4a8' : '#7fb0d0', xx, -28 + s * 5, 3, 3);
    rect(c, '#9aa0a6', w / 2 - 26, -38, 1.4, 8);
    if (night) ellipse(c, 'rgba(255,220,160,0.5)', w / 2 - 25, -39, 1.4, 1.4);
  });
}

/** Cijin across the water: a low green strip with the lighthouse, palms and houses on the far shore. */
function cijin(c: Ctx, d: Decor): void {
  const w = d.w ?? 256, h = d.h ?? 40, x = d.x, y = d.y;
  art(c, `khCijin:${w}`, x, y, w, h, (c, night) => {
    const L = -w / 2, R = w / 2;
    // Land.
    rect(c, '#e8d8a8', L, -8, w, 8);
    rect(c, '#5a9a46', L, -14, w, 7);
    for (let xx = L; xx < R; xx += 5) ellipse(c, '#3f7a36', xx + 2, -14 + (Math.round(xx) % 3), 3.5, 2.6);
    // Houses.
    for (let xx = L + 20; xx < R - 20; xx += 34) {
      const col = ['#f4f1ea', '#e8d8c8', '#d8e4e8'][Math.abs(Math.round(xx / 34)) % 3];
      block(c, xx, -24, 16, 10, col, shade(col, 0.1), shade(col, -0.3));
      rect(c, '#c8402e', xx - 1, -26, 18, 2.4);
      rect(c, night ? '#ffe4a8' : '#7fb0d0', xx + 4, -22, 3, 3); rect(c, night ? '#ffe4a8' : '#7fb0d0', xx + 9, -22, 3, 3);
    }
    // Palms.
    for (let xx = L + 10; xx < R; xx += 46) {
      line(c, '#7a5a3a', 1.6, [xx, -12, xx + 2, -28]);
      for (let k = 0; k < 6; k++) line(c, '#3f8a3a', 1.4, [xx + 2, -28, xx + 2 + Math.cos(k * 1.05) * 8, -28 + Math.sin(k * 1.05) * 5 - 2]);
    }
    // Lighthouse (旗後燈塔): white octagonal tower, black lantern.
    const lx = 0.3 * w;
    block(c, lx - 5, -42, 10, 30, '#f8f6f0', '#ffffff', '#a8a098');
    block(c, lx - 6, -46, 12, 4, '#2a2e33', '#4a4e54');
    rect(c, night ? '#ffffb0' : '#9fcbe3', lx - 3, -45, 6, 3);
    poly(c, '#2a2e33', [lx - 6, -46, lx + 6, -46, lx, -52]);
    if (night) {
      ellipse(c, 'rgba(255,255,200,0.35)', lx, -44, 14, 6);
      poly(c, 'rgba(255,255,200,0.12)', [lx, -44, lx - 60, -60, lx - 60, -30]);
    }
  });
}

// ---------------------------------------------------------------------------
// Interactive objects
// ---------------------------------------------------------------------------

/** KMRT entrance: red roundel pillar, glass canopy, stairs going down. */
function mrtEntrance(c: Ctx, r: Rect, name: string): void {
  const cx = r.x + r.w / 2, by = r.y + r.h, w = r.w + 8, night = isNight();
  art(c, `khMrt:${name}`, cx, by, w, 50, (c, night) => {
    shadow(c, 0, 0, w / 2, 3, 0.3);
    block(c, -w / 2, -6, w, 6, '#5a6068', '#7a8088', '#3a4048');
    rect(c, night ? '#ffd27a' : '#2a3038', -w / 2 + 4, -5, w - 8, 5);
    for (let k = 0; k < 3; k++) rect(c, `rgba(255,255,255,${0.12 + k * 0.08})`, -w / 2 + 4, -5 + k * 1.6, w - 8, 0.7);
    for (const px of [-w / 2 + 1, w / 2 - 3]) rect(c, '#9aa0a6', px, -30, 2, 24);
    rect(c, night ? 'rgba(255,230,180,0.4)' : 'rgba(170,210,235,0.5)', -w / 2 + 3, -28, w - 6, 18);
    block(c, -w / 2 - 2, -34, w + 4, 5, '#e8ecef', '#ffffff', '#8a9096');
    // Roundel pillar.
    block(c, w / 2 - 2, -44, 6, 38, '#f4f6f8', '#ffffff', '#8a9096');
    ellipse(c, '#c8402e', w / 2 + 1, -40, 4, 4);
    ellipse(c, '#ffffff', w / 2 + 1, -40, 2.8, 2.8);
    ellipse(c, '#c8402e', w / 2 + 1, -40, 1.4, 1.4);
    txt(c, '捷運', w / 2 + 1, -30, 3.4, '#c8402e', 900);
    txt(c, name, 0, -31.6, 3.2, '#1f3a6e', 900);
  });
  if (night) ellipse(c, 'rgba(255,230,180,0.2)', cx, by - 20, w / 2, 10);
}

/** Vehicle rental kiosk: awning, counter, bikes and e-scooters lined up for hire. */
function rentShop(c: Ctx, r: Rect, t: number): void {
  const x = r.x, y = r.y + r.h, w = r.w;
  shadow(c, x + w / 2, y, w / 2 + 6, 3, 0.3);
  block(c, x, y - 30, w, 30, '#f6efe2', '#ffffff', '#a89888');
  rect(c, isNight() ? '#ffe4a8' : '#cfe4ee', x + 4, y - 26, w - 8, 12);
  block(c, x + 2, y - 12, w - 4, 12, '#8a5530', '#a8704a', '#5a3520');
  for (let i = 0; i < w; i += 6) rect(c, (i / 6) % 2 ? '#fff6e6' : '#2fa36a', x - 2 + i, y - 36, Math.min(6, w + 4 - i), 7);
  sign(c, '租車 RENT', x + w / 2, y - 48, 44, '#2fa36a');
  // Bikes and scooters by the kiosk.
  for (let k = 0; k < 2; k++) {
    const bx = x + w + 6 + k * 12;
    ellipse(c, '#1b1d24', bx - 3, y - 2, 2.6, 2.6); ellipse(c, '#1b1d24', bx + 3, y - 2, 2.6, 2.6);
    line(c, '#f07a1a', 1.2, [bx - 3, y - 2, bx, y - 7, bx + 3, y - 2]);
    line(c, '#f07a1a', 1.2, [bx, y - 7, bx + 1, y - 9]);
  }
  block(c, x - 12, y - 8, 9, 6, '#57c278', '#7ad098', '#2a7a4a');
  ellipse(c, '#1b1d24', x - 10, y - 1.5, 1.6, 1.6); ellipse(c, '#1b1d24', x - 5, y - 1.5, 1.6, 1.6);
  rect(c, '#1b1d24', x - 6, y - 14, 0.8, 7);
  void t;
}

/** Cash machine lobby: blue sign, glowing screen. */
function atm(c: Ctx, r: Rect): void {
  const x = r.x, y = r.y + r.h, w = r.w, night = isNight();
  block(c, x - 2, y - 30, w + 4, 30, '#dfe8f2', '#ffffff', '#8aa0b6');
  block(c, x - 3, y - 36, w + 6, 7, '#1f4fa8', '#3a6fc8', '#102a60');
  txt(c, 'ATM 提款', x + w / 2, y - 32.4, 3.6, '#ffffff', 900);
  block(c, x + 2, y - 24, w - 4, 18, '#9aa4b0', '#c8d0d8', '#5a6470');
  rect(c, night ? '#7fd0ff' : '#5fa0d0', x + 4, y - 22, w - 8, 7);
  rect(c, '#2a3038', x + 4, y - 12, w - 8, 2);
  if (night) ellipse(c, 'rgba(160,220,255,0.2)', x + w / 2, y - 14, w, 10);
}

/** Public toilet block: tiled box, blue pictogram sign. */
function toilet(c: Ctx, r: Rect): void {
  const x = r.x, y = r.y + r.h, w = r.w;
  block(c, x - 2, y - 28, w + 4, 28, '#d8e4e8', '#eef4f6', '#8aa0a8');
  for (let yy = y - 26; yy < y - 2; yy += 3) rect(c, 'rgba(0,0,0,0.06)', x - 2, yy, w + 4, 0.5);
  rect(c, '#2a3038', x + 3, y - 20, w - 6, 20);
  block(c, x - 1, y - 34, w + 2, 7, '#1f4fa8', '#3a6fc8', '#102a60');
  ellipse(c, '#ffffff', x + 5, y - 32, 1, 1); rect(c, '#ffffff', x + 4, y - 31, 2, 3);
  ellipse(c, '#ffffff', x + w - 5, y - 32, 1, 1); poly(c, '#ffffff', [x + w - 7, y - 28, x + w - 3, y - 28, x + w - 5, y - 31]);
  txt(c, '廁所', x + w / 2, y - 30.6, 3, '#ffffff', 900);
}

/** Coin telescope and an information board. */
function lookout(c: Ctx, r: Rect, title: string, en: string): void {
  const cx = r.x + r.w / 2, by = r.y + r.h;
  shadow(c, cx, by, r.w / 2, 2);
  for (const px of [cx - 12, cx - 2]) block(c, px, by - 16, 2.2, 16, '#5a4a3a', '#7a6a58');
  block(c, cx - 16, by - 30, 24, 15, '#1f3a6e', '#2f5a9e', '#102040');
  txt(c, title, cx - 4, by - 24.4, 4.4, '#fff6dc', 900);
  txt(c, en, cx - 4, by - 18.8, 2.2, '#cfe0ff', 700);
  rect(c, '#3a3e44', cx + 10, by - 14, 2, 14);
  block(c, cx + 6, by - 20, 10, 6, '#c8402e', '#e05a48', '#8a2a1e');
  ellipse(c, '#1b1d24', cx + 16, by - 17, 1.4, 2);
}

/** Pier-2's entrance banner hung on the warehouse doors. */
function pier2Banner(c: Ctx, r: Rect): void {
  const cx = r.x + r.w / 2, by = r.y + r.h, night = isNight();
  rect(c, night ? '#ffe4a8' : '#2a2a30', r.x, by - 24, r.w, 24);
  rect(c, '#8a4a2a', cx - 0.6, by - 24, 1.2, 24);
  block(c, cx - 30, by - 44, 60, 14, '#1b1d24', '#3a3e44', '#0e1014');
  txt(c, '駁二藝術特區', cx, by - 38.4, 6, '#ffd34e', 900);
  txt(c, 'PIER-2 ART CENTER', cx, by - 32.6, 2.6, '#ffffff', 700);
  for (let k = 0; k < 6; k++) ellipse(c, ART_COLS[k], cx - 25 + k * 10, by - 46, 1.6, 1.6);
}

/** The giant worker and fisherwoman statues of Pier-2: blocky, colourful, 60 px tall. */
function giants(c: Ctx, r: Rect): void {
  const cx = r.x + r.w / 2, by = r.y + r.h;
  art(c, 'khGiants', cx, by, r.w + 24, 64, (c) => {
    shadow(c, 0, 0, r.w / 2 + 8, 3.5, 0.3);
    block(c, -r.w / 2 - 6, -4, r.w + 12, 4, '#9a9690', '#b8b4ac', '#6a6660');
    // Worker: blue overalls, yellow helmet, big square hands.
    const wx = -14;
    block(c, wx - 6, -28, 12, 24, '#2f6fb0', '#4a8ad0', '#1f4a80');
    block(c, wx - 9, -40, 18, 14, '#f4a060', '#ffc090', '#b06030');
    block(c, wx - 7, -52, 14, 12, '#f4a060', '#ffc090', '#b06030');
    block(c, wx - 8, -58, 16, 7, '#ffd34e', '#ffe890', '#b89020');
    rect(c, '#1b1d24', wx - 4, -48, 2, 2); rect(c, '#1b1d24', wx + 2, -48, 2, 2);
    rect(c, '#1b1d24', wx - 3, -44, 6, 1.2);
    block(c, wx - 15, -40, 6, 16, '#f4a060', '#ffc090', '#b06030');
    block(c, wx + 9, -40, 6, 16, '#f4a060', '#ffc090', '#b06030');
    block(c, wx - 6, -8, 5, 4, '#1b1d24'); block(c, wx + 1, -8, 5, 4, '#1b1d24');
    // Fisherwoman: red headscarf, striped top, a basket of fish.
    const fx = 16;
    block(c, fx - 6, -28, 12, 24, '#2f3442', '#4a5060', '#1b1d24');
    block(c, fx - 9, -40, 18, 14, '#f4f1ea', '#ffffff', '#b8b0a0');
    for (let yy = -39; yy < -27; yy += 3) rect(c, '#2f6fb0', fx - 9, yy, 18, 1.2);
    block(c, fx - 7, -52, 14, 12, '#f0c8a4', '#f8dcc4', '#b08060');
    block(c, fx - 8, -58, 16, 8, '#c8402e', '#e05a48', '#8a2a1e');
    poly(c, '#c8402e', [fx + 8, -56, fx + 14, -50, fx + 8, -50]);
    rect(c, '#1b1d24', fx - 4, -48, 2, 2); rect(c, '#1b1d24', fx + 2, -48, 2, 2);
    ellipse(c, '#e2493b', fx, -44, 2, 1);
    block(c, fx - 15, -40, 6, 16, '#f0c8a4', '#f8dcc4', '#b08060');
    block(c, fx + 9, -40, 6, 16, '#f0c8a4', '#f8dcc4', '#b08060');
    block(c, fx + 8, -30, 12, 8, '#c8a060', '#e0c080', '#8a6a30');
    for (let k = 0; k < 3; k++) ellipse(c, '#7fb0d0', fx + 11 + k * 3, -31, 1.6, 0.9);
    block(c, fx - 6, -8, 5, 4, '#1b1d24'); block(c, fx + 1, -8, 5, 4, '#1b1d24');
  }, false);
}

/** LRT stop pole with the C-number, and a ticket machine. */
function lrtPole(c: Ctx, r: Rect, code: string): void {
  const cx = r.x + r.w / 2, by = r.y + r.h, night = isNight();
  shadow(c, cx, by, r.w / 2, 2);
  block(c, cx - 14, by - 22, 12, 22, '#2fa36a', '#4fc38a', '#1f7a4a');
  rect(c, night ? '#7fd0ff' : '#5fa0d0', cx - 12, by - 20, 8, 6);
  rect(c, '#1b1d24', cx - 12, by - 12, 8, 1.4);
  rect(c, '#5a6470', cx + 6, by - 36, 1.8, 36);
  ellipse(c, '#2fa36a', cx + 7, by - 40, 6, 6);
  ellipse(c, '#ffffff', cx + 7, by - 40, 4.6, 4.6);
  txt(c, code, cx + 7, by - 40, 4, '#2fa36a', 900);
}

/** Ferry turnstile at the end of the pier with the fare board. */
function ferryGate(c: Ctx, r: Rect): void {
  const cx = r.x + r.w / 2, by = r.y + r.h;
  block(c, r.x, by - 10, r.w, 10, '#c8ccd0', '#e8ecef', '#7a8088');
  for (let x = r.x + 3; x < r.x + r.w - 2; x += 8) rect(c, '#3a3e44', x, by - 16, 1.4, 6);
  rect(c, '#f07a1a', r.x + 2, by - 17, r.w - 4, 1.6);
  block(c, cx - 10, by - 30, 20, 11, '#1f4fa8', '#3a6fc8', '#102a60');
  txt(c, '渡輪 FERRY', cx, by - 26.6, 3, '#ffffff', 900);
  txt(c, '全票 NT$40', cx, by - 22.4, 2.2, '#ffd34e', 800);
}

/** Ice-cream cart with a striped parasol and cones. */
function iceCart(c: Ctx, r: Rect, t: number): void {
  const x = r.x, y = r.y + r.h, w = r.w;
  shadow(c, x + w / 2, y, w / 2 + 2, 3, 0.3);
  ellipse(c, '#1b1d24', x + 6, y - 1, 3, 3); ellipse(c, '#1b1d24', x + w - 6, y - 1, 3, 3);
  block(c, x, y - 18, w, 15, '#f4f6f8', '#ffffff', '#9aa0a6');
  rect(c, '#ff9fc0', x, y - 18, w, 3);
  block(c, x + 3, y - 14, w - 6, 6, '#7fd0ff', '#b0e8ff', '#3a90c0');
  for (let k = 0; k < 4; k++) {
    const cxk = x + 6 + k * (w - 12) / 3;
    poly(c, '#e0a860', [cxk - 2, y - 20, cxk + 2, y - 20, cxk, y - 14]);
    ellipse(c, ['#ff9fc0', '#f4f1ea', '#8a5530', '#57c278'][k], cxk, y - 21, 2.4, 2.4);
  }
  rect(c, '#9aa0a6', x + w / 2, y - 40, 1, 22);
  for (let k = 0; k < 6; k++) poly(c, k % 2 ? '#ff6aa0' : '#ffffff', [x + w / 2, y - 44, x + w / 2 - 20 + k * 6.7, y - 36, x + w / 2 - 20 + (k + 1) * 6.7, y - 36]);
  sign(c, '旗津冰', x + w / 2, y - 56, 36, '#e05a8a');
  void t;
}

/** Station doors seen up close: the sliding glass and the exit sign (the façade itself is decor). */
function hsrDoors(c: Ctx, r: Rect, t: number): void {
  const night = isNight();
  if (night) ellipse(c, 'rgba(255,230,180,0.25)', r.x + r.w / 2, r.y + r.h, r.w / 2 + 10, 8);
  const slide = Math.max(0, Math.sin(t * 0.8)) * 6;
  rect(c, 'rgba(255,255,255,0.25)', r.x + r.w / 2 - 18 - slide, r.y + r.h - 28, 2, 27);
  rect(c, 'rgba(255,255,255,0.25)', r.x + r.w / 2 + 16 + slide, r.y + r.h - 28, 2, 27);
}

const OBJECTS: Record<string, (c: Ctx, r: Rect, t: number) => void> = {
  kh_hsr: hsrDoors,
  bank_atmKh: atm,
  wc_kh: toilet,
  rent_kh: rentShop,
  kh_mrtZuoying: (c, r) => mrtEntrance(c, r, '左營站 R16'),
  kh_mrtYancheng: (c, r) => mrtEntrance(c, r, '鹽埕埔站 O2'),
  kh_lotusView: (c, r) => lookout(c, r, '蓮池潭', 'LOTUS POND · 42 ha'),
  kh_tower85: (c, r) => lookout(c, r, '85大樓', 'SKY TOWER · 347 m'),
  kh_papaya: (c, r, t) => marketStall(c, r, t, 5),
  kh_oyster: (c, r, t) =>
    stall(c, r, t, '蚵仔煎', '#f2a030', (c, r, t) => {
      block(c, r.x + 4, r.y - 16, r.w - 8, 7, '#2a2a30', '#4a4a50', '#15171b');
      for (let k = 0; k < 2; k++) {
        ellipse(c, '#f4d070', r.x + 12 + k * 18, r.y - 13, 7, 3);
        for (let j = 0; j < 4; j++) ellipse(c, j % 2 ? '#3f8a3a' : '#8a7a6a', r.x + 8 + k * 18 + j * 2.6, r.y - 13.6, 1.1, 0.8);
        ellipse(c, '#e05a2a', r.x + 12 + k * 18, r.y - 12, 2.4, 0.9);
      }
      steam(c, r.x + r.w / 2, r.y - 17, t, 4, 0.45);
    }),
  kh_squid: (c, r, t) => marketStall(c, r, t, 1),
  kh_loveBoat: (c, r) => {
    const cx = r.x + r.w / 2, by = r.y + r.h;
    block(c, cx - 8, by - 20, 16, 20, '#f4f6f8', '#ffffff', '#9aa0a6');
    rect(c, isNight() ? '#ffe4a8' : '#cfe4ee', cx - 6, by - 17, 12, 8);
    block(c, cx - 11, by - 26, 22, 7, '#e05a8a', '#ff8ab0', '#a03a5a');
    txt(c, '售票 TICKETS', cx, by - 22.6, 2.8, '#ffffff', 900);
    txt(c, 'NT$150', cx, by - 7, 3, '#e05a8a', 900);
  },
  kh_pier2: pier2Banner,
  kh_giants: giants,
  kh_lrt: (c, r) => lrtPole(c, r, 'C12'),
  kh_lrtHamasen: (c, r) => lrtPole(c, r, 'C14'),
  kh_ferry: ferryGate,
  kh_icecream: iceCart,
};

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

function drawDecor(c: Ctx, d: Decor, t: number): boolean {
  if (!d.kind.startsWith('kh_')) return false;
  switch (d.kind) {
    case 'kh_hsrStation': hsrStation(c, d); return true;
    case 'kh_apartments': apartments(c, d); return true;
    case 'kh_shophouse': shophouse(c, d); return true;
    case 'kh_towers': towers(c, d, t); return true;
    case 'kh_lowRise': lowRise(c, d); return true;
    case 'kh_pagoda': pagoda(c, d); return true;
    case 'kh_causeway': causeway(c, d); return true;
    case 'kh_pavilion': pavilion(c, d); return true;
    case 'kh_xuantian': xuantian(c, d); return true;
    case 'kh_railing': railing(c, d); return true;
    case 'kh_templeGate': templeGate(c, d); return true;
    case 'kh_incense': incense(c, d, t); return true;
    case 'kh_glints': glints(c, d, t); return true;
    case 'kh_taxiSign': taxiSign(c, d); return true;
    case 'kh_signpost': signpost(c, d); return true;
    case 'kh_streetSign': streetSign(c, d); return true;
    case 'kh_laneArrow': laneArrow(c, d); return true;
    case 'kh_bridgeRail': bridgeRail(c, d); return true;
    case 'kh_busStop': busStop(c, d); return true;
    case 'kh_store': store(c, d); return true;
    case 'kh_temple': temple(c, d); return true;
    case 'kh_tea': teaKiosk(c, d, t); return true;
    case 'kh_pavilionSmall': pavilionSmall(c, d); return true;
    case 'kh_exercise': exercise(c, d, t); return true;
    case 'kh_taichi': taichi(c, d, t); return true;
    case 'kh_sculpture': sculpture(c, d); return true;
    case 'kh_domeSign': domeSign(c, d); return true;
    case 'kh_glassEntrance': glassEntrance(c, d); return true;
    case 'kh_liuheGate': liuheGate(c, d, t); return true;
    case 'kh_stall': marketStall(c, { x: d.x - (d.w ?? 48) / 2, y: d.y - 12, w: d.w ?? 48, h: 12 }, t, d.variant ?? 0); return true;
    case 'kh_tables': tables(c, d, t); return true;
    case 'kh_stools': stools(c, d); return true;
    case 'kh_loveBoat': loveBoat(c, d, t); return true;
    case 'kh_pedalBoat': pedalBoat(c, d, t); return true;
    case 'kh_dock': dock(c, d); return true;
    case 'kh_heartRail': heartRail(c, d); return true;
    case 'kh_cafe': cafe(c, d); return true;
    case 'kh_riverGlow': riverGlow(c, d, t); return true;
    case 'kh_cyclist': cyclist(c, d, t); return true;
    case 'kh_jogger': jogger(c, d, t); return true;
    case 'kh_mural': mural(c, d); return true;
    case 'kh_containers': containers(c, d); return true;
    case 'kh_graffitiWall': graffitiWall(c, d); return true;
    case 'kh_artist': artist(c, d, t); return true;
    case 'kh_lrtStop': lrtStop(c, d); return true;
    case 'kh_tram': tram(c, d, t); return true;
    case 'kh_trackPosts': trackPosts(c, d); return true;
    case 'kh_skater': skater(c, d, t); return true;
    case 'kh_tower85': tower85(c, d); return true;
    case 'kh_bollard': bollard(c, d); return true;
    case 'kh_lifebuoy': lifebuoy(c, d); return true;
    case 'kh_fisherman': fisherman(c, d, t); return true;
    case 'kh_surfboards': surfboards(c, d); return true;
    case 'kh_ferrySign': ferrySign(c, d); return true;
    case 'kh_gull': gulls(c, d, t); return true;
    case 'kh_kites': kites(c, d, t); return true;
    case 'kh_picnic': picnic(c, d, t); return true;
    case 'kh_surf': surf(c, d, t); return true;
    case 'kh_pierKh': pierKh(c, d); return true;
    case 'kh_ferry': ferry(c, d, t); return true;
    case 'kh_fishBoat': fishBoat(c, d, t); return true;
    case 'kh_cargoShip': cargoShip(c, d); return true;
    case 'kh_cijin': cijin(c, d); return true;
    default:
      // Shared street life: kh_crowd, kh_queue, kh_benchSit, kh_person, kh_cables, kh_vsign, kh_manhole…
      return drawLife(c, d.kind.slice(3), d, t);
  }
}

registerPropDrawers({
  objects: OBJECTS,
  decor: drawDecor,
  animatedObjects: ['kh_hsr', 'kh_papaya', 'kh_oyster', 'kh_squid', 'kh_icecream', 'rent_kh'],
  animatedDecor: [
    'kh_towers', 'kh_incense', 'kh_glints', 'kh_tea', 'kh_exercise', 'kh_taichi', 'kh_liuheGate', 'kh_stall', 'kh_tables',
    'kh_loveBoat', 'kh_pedalBoat', 'kh_riverGlow', 'kh_cyclist', 'kh_jogger', 'kh_artist', 'kh_tram', 'kh_skater',
    'kh_fisherman', 'kh_gull', 'kh_kites', 'kh_picnic', 'kh_surf', 'kh_ferry', 'kh_fishBoat',
    ...LIFE_ANIMATED.map((k) => `kh_${k}`),
  ],
});
